// Athens, "Easter night under the Acropolis": the night of the Resurrection on Monastiraki Square, bitter-orange trees
// in blossom. We stand on the north side of the square and face south: the church of the Pantanassa (the "little
// monastery") at our left hand, the Tzistarakis mosque on the south-east corner, Areos street running south past the
// columns of Hadrian's Library, the 1895 station on the south side, the flea-market lane (Ifestou) to its right, and
// above the roofs of Plaka the floodlit rock: the north wall, the Erechtheion with its north porch in front of the
// Parthenon's north colonnade (17 columns, the west pediment turned a little towards us), the Propylaia at the west end.
// Back to front: night sky with a waning Easter moon and drifting cloud -> Hymettus -> fireworks (behind the rock) ->
// the Acropolis -> Plaka's houses climbing to the rock, the mosque, the Library, the station, the lane's stalls, a
// taverna block with a roof terrace -> the square with its crowd (strollers, a bouzouki and guitar duo and their ring
// of listeners who break into a line dance, a souvlaki queue, a fruit cart, kids with a ball, a balloon seller,
// tourists with cameras, cats, pigeons, people out of the metro) -> the church front and the people waiting outside it
// with candles -> an orange bough overhead, white petals falling. At "midnight" the church goes dark, the priest
// brings the flame to the door, it passes from candle to candle, the bell peals and fireworks open over the rock.
// A rooftop sign for a summer cinema stands under the Parthenon (its face is the exported `billboard`;
// render(t, { pitch }) swaps the ad, render(t, { billboard: false }) leaves the sign and its frame out).
// render(t, { crowd }) thins the crowd: 1 = everyone, just above 0 = only the cast the night needs, 0 = nobody: an
// empty square, its stalls lit and unattended (CROWD below is the default).
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, inEvent, cycle, hash, rng, bayer, hex,
  makeCanvas, px, rect, ditherFill, makeHalo, drawHalo, glyph, text, makePitch } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                          // this scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
const GROUND = 186;                 // far edge of the square: the foot of the mosque, the station and the taverna
// The Parthenon's north flank: 17 Doric columns 3 px thick, 6 px apart; the cornice row, the capitals' (abacus) row, the
// shafts' last row above the steps; the west front's x span and its pediment's apex. As built, the colonnade and its
// entablature stand about a fifth of the flank's length (20 px on 99) and a column about 5 times its thickness.
const PAR = { x0: 186, n: 17, pitch: 6, ent: 40, cap: 46, base: 59, fx0: 285, fx1: 299, apex: 36 };
const ERE = { x0: 236, x1: 259, top: 56, base: 64, porch: 260 };      // the Erechtheion, lower, in front of it on the north edge
const PROP = { x0: 352, x1: 394 };                                    // the Propylaia at the west end
const MOON = { x: 152, y: 27, r: 7 };
const MOSQ = { x0: 76, x1: 128, cx: 102, top: 134, drum: 124, r: 17, pod: 170, px1: 151 };   // Tzistarakis mosque
const STN = { x0: 216, x1: 344, ridge: 152, eave: 163, doors: [228, 252, 280, 308, 332] };   // the old station
const LANE = { x0: 346, x1: 386 };                                    // Ifestou, the flea-market lane
const BLOCK = { x0: 388, top: 114 };                                  // the taverna block with the roof terrace
const LEFTB = { x1: 66, top: 118 };                                   // the corner house with a roof bar, behind the church
const CH = { x1: 52, apex: 26, top: 144, eave: 164, foot: 240, door: { x: 18, w: 14, y: 208 } };   // the Pantanassa's front
const BELL = { x: 26, y: 132 };                                       // the bell in its little belfry
const BB = { x: 222, y: 126, w: 48, h: 20 };                          // the cinema sign's face (the pitch is painted here)
const CART = { x: 150, y: 207 };                                      // fruit and nut cart (left edge, feet row)
const DUO = { x: 262, y: 222 };                                       // the two musicians
const SOUV = { x: 396, w: 28 };                                       // the souvlaki window in the taverna block
const personH = y => Math.round(12 + (y - 190) * 0.2);                // how tall a grown-up is with feet on row y
// The share of the crowd the scene shows by default: 1 = everyone, 0.5 = about half, anything just above 0 = only the
// cast the night's story needs (the duo, the troupe, the priest and a small flock, the vendors and cooks, the kids
// with the ball and whoever a timed event is about), 0 = nobody. Tune this number per city; render(t, { crowd })
// overrides it for one frame.
const CROWD = 0.3;
// Everyone who can thin gets a fixed rank in (0, 1) once, at build time, and is drawn while rank < crowd. The
// essential cast has the rank CAST: there at every crowd level above 0, gone at crowd 0, which means nobody at all
// (with them go the things only a person explains: instruments, candles, balloons, the ball, the lantern's light).
// Rank 0 is for what is not a person and is always drawn (trees, lamps, the cart, the tables, the animals).
// spread(i) ranks the i-th member (or unit: a couple, a parent and child) of a group along the golden-ratio sequence,
// so a group thins evenly over the frame and never from one corner first.
const CAST = -1;
const spread = i => frac((i + 0.5) * 0.6180339887);
const seen = (rank, crowd) => rank === 0 || (crowd > 0 && rank < crowd);

/* ================= Pixel helpers ================= */
// ImageData painter for pixel-heavy layers
function painter(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data, cache = {};
  const put = (i, y, col, a = 255) => {
    i = Math.round(i); y = Math.round(y);
    if (i < 0 || y < 0 || i >= w || y >= h) return;
    const rgb = cache[col] || (cache[col] = hex(col)), o = (y * w + i) * 4;
    d[o] = rgb[0]; d[o + 1] = rgb[1]; d[o + 2] = rgb[2]; d[o + 3] = a;
  };
  const alpha = (i, y) => (i < 0 || y < 0 || i >= w || y >= h) ? 0 : d[(y * w + i) * 4 + 3];
  const rgb = (i, y) => { const o = (y * w + i) * 4; return (d[o] << 16) | (d[o + 1] << 8) | d[o + 2]; };
  const box = (x0, y0, bw, bh, col) => { for (let y = y0; y < y0 + bh; y++) for (let i = x0; i < x0 + bw; i++) put(i, y, col); };
  return { c, x, put, alpha, rgb, box, done: () => { x.putImageData(img, 0, 0); return c; } };
}
// integer Bresenham line
function line(c, x0, y0, x1, y1, col) {
  x0 = Math.round(x0); y0 = Math.round(y0); x1 = Math.round(x1); y1 = Math.round(y1);
  const dx = Math.abs(x1 - x0), dy = -Math.abs(y1 - y0), sx = x0 < x1 ? 1 : -1, sy = y0 < y1 ? 1 : -1;
  let err = dx + dy;
  c.fillStyle = col;
  for (;;) {
    c.fillRect(x0, y0, 1, 1);
    if (x0 === x1 && y0 === y1) break;
    const e2 = 2 * err;
    if (e2 >= dy) { err += dy; x0 += sx; }
    if (e2 <= dx) { err += dx; y0 += sy; }
  }
}
// sprite from rows of characters and a colour key ('.' or unknown = transparent)
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = painter(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.put(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
function stamp(p, rows, key, x0, y0) {
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.put(x0 + i, y0 + y, col); } });
}
// dithered soft blob for smoke and steam
function makePuff(r, light, dark) {
  const s = r * 2 + 1, p = painter(s, s);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const d = Math.hypot(i - r, y - r) / (r + 0.5);
    if (d < 1 && (1 - d) * 1.8 > bayer(i, y) * 0.95) p.put(i, y, i + y < r * 1.7 ? light : dark);
  }
  return p.done();
}
const tintCache = {};
function tint(col, tcol, a) {                       // #rrggbb between two colours
  const k = col + tcol + a;
  if (!tintCache[k]) {
    const A = hex(col), B = hex(tcol);
    tintCache[k] = '#' + [0, 1, 2].map(j => Math.round(A[j] + (B[j] - A[j]) * a).toString(16).padStart(2, '0')).join('');
  }
  return tintCache[k];
}
const darker = (c, a = 0.38) => tint(c, '#05040a', a);
const pick = (ramp, v, i, y) => ramp[clamp(Math.floor(v * (ramp.length - 1) + bayer(i, y)), 0, ramp.length - 1)];
function vnoise(x, y, s) {                          // smooth value noise in [0, 1)
  const xi = Math.floor(x), yi = Math.floor(y), fx = x - xi, fy = y - yi, u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  return lerp(lerp(hash(xi, yi, s), hash(xi + 1, yi, s), u), lerp(hash(xi, yi + 1, s), hash(xi + 1, yi + 1, s), u), v);
}
// Greek capitals for the signs, typed with Latin keys: the letters that look the same come from core's font, the
// rest are drawn here (S sigma, L lambda, D delta, G gamma, Q theta, F phi, W omega, U pi, J psi, C xi).
const GREEK = {
  S: ['111', '100', '010', '100', '111'], L: ['010', '101', '101', '101', '101'], D: ['010', '010', '101', '101', '111'],
  G: ['111', '100', '100', '100', '100'], Q: ['010', '101', '111', '101', '010'], F: ['010', '111', '101', '111', '010'],
  W: ['010', '101', '101', '010', '101'], U: ['111', '101', '101', '101', '101'], J: ['101', '101', '111', '010', '010'],
  C: ['111', '000', '111', '000', '111'],
};
function greek(c, s, x, y, col) {
  let cx = x;
  for (const ch of s) { if (GREEK[ch]) glyph(c, GREEK[ch], cx, y, col); else text(c, ch, cx, y, col); cx += 4; }
  return cx - x - 1;
}

/* ================= Sky, stars, the moon ================= */
const SKY = ['#04061a', '#060920', '#080c28', '#0b1030', '#0f1438', '#141940', '#1a1e48', '#21234f', '#2a2855', '#352e5a',
  '#42345c', '#523b5c'];
function renderSky() {
  return ditherFill(W, H, SKY, (i, y) => {
    let v = Math.pow(Math.min(1, y / 150), 1.35) * 0.86;
    v += Math.max(0, 1 - Math.hypot((i - 245) / 270, (y - 108) / 96)) * 0.24;   // the floodlit rock's gold in the haze
    return v;
  });
}
const STARS = [];
{ const r = rng(57); for (let i = 0; i < 46; i++) STARS.push({ x: Math.floor(r() * W), y: 1 + Math.floor(r() * 92), k: 18 + Math.floor(r() * 50), ph: r(), b: 0.3 + r() * 0.6 }); }
// a waning gibbous moon (Easter falls just after the full moon): lit from the left, the right limb in shadow
function renderMoon() {
  const r = MOON.r, s = r * 2 + 1, p = painter(s, s);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - r, dy = y - r, d = Math.hypot(dx, dy);
    if (d > r + 0.3) continue;
    const lim = Math.sqrt(Math.max(0, r * r - dy * dy));
    if (dx > lim * 0.45 + 0.5) { if (d < r - 0.4) p.put(i, y, '#161b3c'); continue; }      // the shadowed limb
    let col = d > r - 1 ? '#d8d2bc' : '#f6f0da';
    if (vnoise(i * 0.55, y * 0.55, 77) > 0.62) col = '#cdc7b2';                               // maria
    if (dx > lim * 0.45 - 1) col = '#a8a493';
    p.put(i, y, col);
  }
  return p.done();
}

/* ================= Clouds (tileable; moonlit tops, bellies warmed by the city) ================= */
const CLOUD_DEFS = [
  { seed: 7, y: 0, h: 46, banks: 4, k: 1, sx: 1.5, pal: { top: '#3c4674', body: '#1b2146', core: '#161b3c', bot: '#2e2c50', bot2: '#23254a' } },
  { seed: 31, y: 26, h: 52, banks: 4, k: 2, sx: 1.35, pal: { top: '#4a5484', body: '#232a54', core: '#1c2248', bot: '#4e3e5c', bot2: '#35325c' } },
  { seed: 53, y: 72, h: 50, banks: 5, k: 1, sx: 1.7, pal: { top: '#50507c', body: '#2c2c56', core: '#25254c', bot: '#6e4e5e', bot2: '#4a3c5a' } },
];
function makeCloudLayer(def) {
  const r = rng(def.seed), h = def.h, puffs = [];
  for (let b = 0; b < def.banks; b++) {
    const bx = r() * W, bw = 70 + r() * 110, base = h * (0.45 + r() * 0.35);
    const np = 6 + Math.floor(r() * 6);
    for (let i = 0; i < np; i++) {
      const rx = (7 + r() * 13) * def.sx, ry = 3.5 + r() * 6;
      puffs.push({ x: bx + (r() - 0.5) * bw, y: base - r() * ry, rx, ry, base: base + ry * 0.3 });
    }
  }
  const D = new Float32Array(W * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let best = -9;
    for (const p of puffs) {
      let dx = x - p.x; dx -= Math.round(dx / W) * W;
      const dy = y - p.y;
      let v = 1 - (dx * dx) / (p.rx * p.rx) - (dy * dy) / (p.ry * p.ry);
      if (y > p.base) v -= (y - p.base) * 0.4;             // flat bellies
      if (v > best) best = v;
    }
    best += 0.14 * Math.sin(TAU * 9 * x / W + y * 0.37 + def.seed) * Math.sin(TAU * 4 * x / W - y * 0.21);
    D[y * W + x] = best;
  }
  const get = (x, y) => (y < 0 || y >= h) ? -9 : D[y * W + ((x % W) + W) % W];
  const p = painter(W, h), P = def.pal;
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const th = (bayer(x, y) - 0.5) * 0.3, v = D[y * W + x];
    if (v + th <= 0.03) continue;
    let col;
    if (get(x, y - 1) + th <= 0.03 || get(x, y - 2) <= 0) col = P.top;
    else if (get(x, y + 1) + th <= 0.03) col = P.bot;
    else if (get(x, y + 3) <= 0.05 + bayer(x, y) * 0.2) col = P.bot2;
    else if (v > 0.55 + th) col = P.core;
    else col = P.body;
    p.put(x, y, col);
  }
  return { c: p.done(), y: def.y, k: def.k };
}

/* ================= Hymettus, far to the east (left), the suburbs' lights at its foot ================= */
const FAR_LIGHTS = [];
function renderFar() {
  const p = painter(W, H), r = rng(19);
  for (let i = 0; i < W; i++) {
    const top = Math.round(112 + 7 * Math.sin(i * 0.021 + 0.6) + 3 * Math.sin(i * 0.07) + (i > 140 ? (i - 140) * 0.1 : 0));
    for (let y = top; y < 150; y++) p.put(i, y, y === top ? '#2a2c58' : y < top + 5 && bayer(i, y) < 0.5 ? '#1c1e46' : '#15173a');
  }
  for (let k = 0; k < 60; k++) {
    const x = Math.floor(r() * 150), y = 122 + Math.floor(r() * 16);
    p.put(x, y, r() < 0.7 ? '#b08a5a' : '#8a94c0');
    if (r() < 0.3) FAR_LIGHTS.push({ x, y, k: 30 + Math.floor(r() * 60), ph: r() });
  }
  return p.done();
}

/* ================= The Acropolis: rock, north wall, Parthenon, Erechtheion, Propylaia ================= */
// outline of the rock with the wall on it, seen from the north: the east end on the left, the Propylaia's bastion and
// the descent to the Areopagus on the right
const ROCK_PTS = [[88, 136], [100, 120], [112, 104], [122, 92], [129, 83], [132, 72], [160, 69], [190, 66], [240, 64], [300, 66],
  [346, 68], [396, 71], [403, 83], [418, 93], [434, 107], [450, 123], [466, 138]];
function rockTop(x) {
  for (let k = 0; k + 1 < ROCK_PTS.length; k++) {
    const [x0, y0] = ROCK_PTS[k], [x1, y1] = ROCK_PTS[k + 1];
    if (x >= x0 && x <= x1) return lerp(y0, y1, (x - x0) / (x1 - x0));
  }
  return 999;
}
const onWall = x => x >= 132 && x <= 396;
const wallH = x => (x >= 346 ? 14 : 11) + Math.round(1.5 * Math.sin(x * 0.045));
const cliffEnd = x => rockTop(x) + (onWall(x) ? wallH(x) : 0) + 22 + 7 * vnoise(x / 14, 0, 9) + (onWall(x) ? 0 : -14);
const MARBLE = ['#8c6a44', '#b0885a', '#d2aa6e', '#ecc888', '#fbe0a4', '#fff2c8'];
const WALLC = ['#46342a', '#66503a', '#8a6c46', '#b08c56', '#d2ae6c', '#eecd88'];
const ROCKC = ['#191626', '#2a2230', '#40313a', '#5a4442', '#785b4c', '#977556', '#b48f62'];
/** the plateau behind the wall, rising to the foot of the Parthenon's steps: the temple stands on ground, not on air */
function drawPlateau(p) {
  const a = PAR.x0 - 3, b = PAR.fx1 + 2, run = 22;
  for (let x = a - run; x <= b + run; x++) {
    const u = x < a ? (a - x) / run : x > b ? (x - b) / run : 0, foot = Math.round(rockTop(x));
    const top = Math.round(lerp(PAR.base + 1, foot, smooth(0, 1, u)));
    for (let y = top; y < foot; y++) p.put(x, y, pick(ROCKC, 0.64 + (y === top ? 0.16 : 0) + (vnoise(x / 5, y / 2, 17) - 0.5) * 0.3, x, y));
  }
}
function drawParthenon(p) {
  const put = p.put, { x0, n, pitch, ent, cap, base, fx0, fx1, apex } = PAR, x1 = x0 + (n - 1) * pitch + 2;   // x1: the NW corner
  const far = cap + 5;                                  // seen from below, the south colonnade's entablature crosses the gaps this low
  // between the columns: the porches in shadow at both ends; the middle, blown out in 1687 and roofless, shows the sky
  // above the south entablature, the south columns against the sky below it (none where the south side's middle fell)
  // and a few rebuilt courses of the cella
  for (let i = x0 + 3; i < x1 - 2; i++) {
    const g = Math.floor((i - x0) / pitch), open = g >= 2 && g < n - 4, gone = g >= 6 && g <= 9, j = (i - x0) % pitch;
    for (let y = cap; y <= base; y++) {
      let col = null;
      if (!open) col = y < cap + 3 ? '#4c3828' : pick(['#5a422e', '#735638', '#8a6a44'], 0.2 + 0.6 * (y - cap) / (base - cap), i, y);
      else if (y > base - 3) col = y === base - 2 ? '#7a5c3c' : '#5a422e';
      else if (gone) continue;
      else if (y === far) col = '#8a6a44';
      else if (y === far + 1) col = '#5e4630';
      else if (y > far && j === 4) col = '#76583a';
      if (col) put(i, y, col);
    }
  }
  for (let k = 0; k < n; k++) {                         // the north colonnade: shafts lit from below, narrowing to the capitals
    const cx = x0 + k * pitch;
    for (let y = cap + 2; y <= base; y++) {
      const low = y > base - 4, hi = y < cap + 5;
      put(cx, y, low ? '#fff6d4' : '#ffeab0'); put(cx + 1, y, low ? '#f8dca0' : '#eccb88'); put(cx + 2, y, hi ? '#a87a44' : '#cc9c5c');
    }
    for (let d = -1; d <= 3; d++) {                     // the capital: a square abacus on the cushion of the echinus
      put(cx + d, cap, d === 3 ? '#d6a45c' : '#fff4cc');
      put(cx + d, cap + 1, d < 0 || d > 2 ? '#a07444' : d === 2 ? '#d6a45c' : '#fbe2a6');
    }
  }
  for (let i = x0 - 1; i <= x1; i++) {                  // the flank's entablature: cornice, frieze of triglyphs and metopes, architrave
    const tri = (i - x0 + 2) % 3 === 0;                 // a triglyph over every column and every gap
    put(i, ent, '#fff6d4'); put(i, ent + 1, '#d2a866');
    put(i, ent + 2, tri ? '#a87a44' : '#f4d898'); put(i, ent + 3, tri ? '#946a3c' : '#e8c684');
    put(i, ent + 4, '#fbe4ac'); put(i, ent + 5, '#ecca88');
  }
  // the west front, turned a little towards us and a shade darker: five more of its columns crowd together at this angle
  for (let i = fx0; i <= fx1; i++) {
    const j = (i - fx0) % 3;
    for (let y = cap; y <= base; y++) put(i, y, j === 0 ? '#4c3828' : y === cap ? '#f4d8a0' : j === 2 ? '#a87c48' : y > base - 4 ? '#f0d29a' : '#e2bc7e');
  }
  for (let i = fx0; i <= fx1 + 1; i++) {                // its entablature, the triglyphs foreshortened
    put(i, ent, '#f4dca4'); put(i, ent + 1, '#b88e56');
    put(i, ent + 2, i & 1 ? '#98703e' : '#dcb878'); put(i, ent + 3, i & 1 ? '#8a6438' : '#d0aa6c');
    put(i, ent + 4, '#e6c888'); put(i, ent + 5, '#d4b070');
  }
  const pediment = (a, b, edge, face) => {              // a low gable on the cornice from x a to x b, its apex on row `apex`
    const mid = (a + b) / 2;
    for (let y = apex; y < ent; y++) {
      const hw = (b - a) / 2 * (y - apex + 0.6) / (ent - apex), l = Math.ceil(mid - hw), r = Math.floor(mid + hw);
      for (let i = l; i <= r; i++) put(i, y, i <= l + 1 || i >= r - 1 || y === apex ? edge : face);
    }
  };
  pediment(x0 - 1, x0 + fx1 - x1 + 1, '#e6c480', '#8a6a44');   // the east pediment, its back to us across the roofless cella
  pediment(x1 - 2, fx1 + 1, '#fff2c8', '#b08a54');              // the west pediment
  for (let s = 0; s < 3; s++) for (let i = x0 - 1 - s; i <= fx1 + 1 + s; i++)   // the crepidoma: three steps, each a pixel wider
    put(i, base + 1 + s, i > x1 ? ['#e6c88c', '#b89058', '#cca870'][s] : ['#f8e0a8', '#d4b070', '#e8c68a'][s]);
}
function drawErechtheion(p) {
  const { x0, x1, top, base, porch } = ERE, own = new Set();
  const put = (i, y, col) => { p.put(i, y, col); own.add(y * W + i); };
  // the main block's north wall: ashlar a tone below the Parthenon's, its frieze of dark Eleusinian stone between a
  // pale cornice and architrave
  for (let i = x0; i <= x1; i++) for (let y = top; y <= base; y++) {
    let col = y === top ? '#e2c286' : y === top + 1 ? '#b08a56' : y === top + 2 ? '#5e4c40' : y === top + 3 ? '#c8a670'
      : pick(['#7a5c3c', '#94744c', '#ae8c5e', '#c4a26c'], 0.35 + 0.45 * (y - top) / (base - top) + (hash(i >> 2, y >> 1, 21) - 0.5) * 0.3, i, y);
    if (y > top + 4 && (y - top) % 3 === 2) col = '#8a6a48';                                    // ashlar courses
    put(i, y, col);
  }
  // the east porch, side-on: two of its Ionic columns under the entablature
  for (let i = x0 - 6; i < x0; i++) { put(i, top, '#e2c286'); put(i, top + 1, '#b08a56'); put(i, top + 2, '#5e4c40'); for (let y = top + 3; y <= base; y++) put(i, y, '#4a3628'); }
  for (const cx of [x0 - 5, x0 - 2]) for (let y = top + 3; y <= base; y++) put(cx, y, y === top + 3 ? '#e8cc94' : '#d0ae76');
  // the north porch stands forward on lower ground: four tall Ionic columns, a little pediment, the great door in shadow
  for (let i = porch; i <= porch + 11; i++) {
    for (let y = top + 4; y <= base + 3; y++) put(i, y, i > porch + 3 && i < porch + 8 && y > top + 6 ? '#6a5038' : '#3e2e24');
    put(i, top + 1, '#e8ca8e'); put(i, top + 2, '#b89058'); put(i, top + 3, '#8a6a44');
  }
  [[5, 6], [3, 8], [1, 10]].forEach(([a, b], k) => { for (let i = a; i <= b; i++) put(porch + i, top - 2 + k, i === a || i === b || k === 0 ? '#e8ca8e' : '#a8845a'); });
  for (const cx of [porch + 1, porch + 4, porch + 7, porch + 10]) for (let y = top + 4; y <= base + 3; y++) put(cx, y, y === top + 4 ? '#f0d49a' : '#dcba80');
  // a dark edge where it stands against the Parthenon's lit columns behind
  for (const k of own) {
    const i = k % W, y = (k - i) / W;
    for (const [dx, dy] of [[-1, 0], [1, 0], [0, -1]]) if (!own.has((y + dy) * W + i + dx)) p.put(i + dx, y + dy, '#3a2a20');
  }
  stamp(p, ['.gG.', 'gGGg', 'GgGG', '.GG.', '..t.'], { g: '#4e7046', G: '#35522f', t: '#4a3a2a' }, porch + 14, base - 5);   // Athena's olive tree
}
function drawPropylaia(p) {
  const put = p.put, { x0, x1 } = PROP;
  for (let i = x0 + 18; i <= x1; i++) for (let y = 50; y <= 72; y++) {                      // the central hall
    let col = y === 50 ? '#fff6d4' : y < 53 ? '#e6c480' : y === 53 ? '#b88c52' : pick(MARBLE, 0.5 + 0.3 * (y - 50) / 22, i, y);
    if (y > 53 && i > x1 - 12) col = '#5a422e';                                              // the west portico in shadow
    put(i, y, col);
  }
  for (const cx of [x1 - 10, x1 - 7, x1 - 4, x1 - 1]) for (let y = 54; y <= 72; y++) put(cx, y, y === 54 ? '#fff6d4' : '#f6dca0');
  for (let i = x0; i <= x0 + 22; i++) for (let y = 56; y <= 72; y++) {                       // the north wing (the Pinakotheke), nearer
    let col = y === 56 ? '#fff8dc' : y === 57 ? '#e6c480' : y === 58 ? '#b88c52' : pick(MARBLE, 0.7 + 0.25 * (y - 56) / 16 + (hash(i >> 2, y >> 1, 23) - 0.5) * 0.3, i, y);
    if (y > 58 && (y - 56) % 4 === 1) col = '#d6b274';
    put(i, y, col);
  }
  for (const wx of [x0 + 6, x0 + 16]) for (let y = 61; y < 65; y++) { put(wx, y, '#5a422e'); put(wx + 1, y, '#6a5038'); }
}

/* ----- the rock under them: the wall with its buttresses, floodlit cliffs, pines and cypresses, Anafiotika ----- */
const FLICK = [];                                     // foliage pixels that stir in the breeze: { x, y, k, ph, col }
const ROCK_LAMPS = [[150, 0], [198, 0], [246, 0], [294, 0], [342, 0], [384, 0]].map(([x]) => ({ x, y: Math.round(cliffEnd(x)) + 3 }));
const ANA_WIN = [];                                   // Anafiotika's windows that switch on and off
const CAVES = [[360, 6], [372, 9], [385, 7]];         // the caves of Apollo, Zeus and Pan under the Propylaia's wing
const TREE_G = ['#0e1814', '#16241a', '#203222', '#2e442c', '#44583a'];
function lampBoost(x, y) {
  let b = 0;
  for (const l of ROCK_LAMPS) if (y <= l.y) b = Math.max(b, 1 - Math.hypot((x - l.x) / 28, (y - l.y) / 40));
  return b * 0.32;
}
function renderAcropolis() {
  const p = painter(W, H), put = p.put, r = rng(404);
  drawPlateau(p); drawParthenon(p); drawPropylaia(p); drawErechtheion(p);
  for (let x = 134; x <= 350; x++) {                  // low ruins and marble blocks along the plateau's edge
    if (x > PAR.x0 - 5 && x < ERE.porch + 20) continue;
    const top = Math.round(rockTop(x));
    if (hash(x >> 1, 1, 31) < 0.42) { put(x, top - 1, '#e6c480'); if (hash(x >> 1, 2, 31) < 0.35) put(x, top - 2, '#fff0c0'); }
  }
  for (let y = 54; y < 71; y++) put(137, y, y === 54 ? '#e8e0c8' : '#a8a090');             // the Belvedere's flagpole (the flag comes down at sunset)
  for (let x = 88; x <= 466; x++) {
    const wall = onWall(x), top = Math.round(rockTop(x) + (wall ? 0 : (vnoise(x / 4, 0, 13) - 0.4) * 7)), wh = wall ? wallH(x) : 0, ce = Math.round(cliffEnd(x));
    const endDim = wall ? 0 : Math.min(0.3, (x < 132 ? 132 - x : x - 396) * 0.012);
    for (let y = top; y < 154; y++) {
      const d = y - top;
      let col;
      if (wall && d < wh) {                             // the north wall: ashlar courses, buttresses, a bright coping
        let v = 0.74 - 0.2 * d / wh + (vnoise(x / 9, y / 4, 11) - 0.5) * 0.25 + lampBoost(x, y) * 0.5;
        const bx = (x - 132) % 24;
        if (bx === 0) v += 0.2; else if (bx === 1) v += 0.08; else if (bx === 2 || bx === 3) v -= 0.16;
        if (d % 3 === 2) v -= 0.08;
        if (d === 0) v = 1;
        col = pick(WALLC, v, x, y);
      } else if (y < ce) {                              // the cliffs, lit from below
        const u = (y - top - wh) / Math.max(1, ce - top - wh);
        let v = 0.2 + 0.46 * vnoise(x / 6, y / 10, 3) + 0.22 * vnoise(x / 2.2, y / 3, 4) + 0.12 * (1 - u) + lampBoost(x, y) - (wall ? 0 : 0.1) - endDim;
        if (hash(x >> 1, 5, 6) < 0.14) v -= 0.14;         // cracks
        if (wall && d === wh) v -= 0.3;                   // shadow at the wall's foot
        col = pick(ROCKC, v, x, y);
      } else {                                          // scrub on the lower slope
        const v = 0.1 + 0.3 * vnoise(x / 8, y / 5, 7) - 0.1 * (y - ce) / 30;
        col = pick(['#10131c', '#171c24', '#1f262c', '#2a3134'], v, x, y);
      }
      put(x, y, col);
    }
  }
  for (let x = 268; x <= 300; x += 4) {               // column drums of the older temple, built into the wall
    const top = Math.round(rockTop(x));
    put(x, top + 3, '#fff0c0'); put(x + 1, top + 3, '#f0d090'); put(x, top + 4, '#f0d090'); put(x + 1, top + 4, '#8a6c46'); put(x + 2, top + 4, '#66503a');
  }
  for (const [cx, dy] of CAVES) {                     // the caves
    const cy = Math.round(rockTop(cx)) + wallH(cx) + dy;
    for (let y = -4; y <= 3; y++) for (let i = -3; i <= 3; i++) if ((i * i) / 10 + (y * y) / 17 < 1) put(cx + i, cy + y, y > 1 ? '#2a2230' : '#120e1a');
  }
  // pines, olives and cypresses on the slopes
  for (let k = 0; k < 120; k++) {
    const x = 92 + Math.floor(r() * 370), ce = cliffEnd(x), y = Math.round(ce - 2 + r() * r() * 34), cyp = r() < 0.22;
    if (y > 150) continue;
    const lit = lampBoost(x, y - 8) > 0.04 || r() < 0.25;
    if (cyp) {
      const hgt = 7 + Math.floor(r() * 6);
      for (let j = 0; j < hgt; j++) {
        const hw = j < 2 ? 0 : 1;
        for (let i = -hw; i <= hw; i++) put(x + i, y - hgt + j, i < 0 && lit ? TREE_G[3] : i > 0 ? TREE_G[0] : TREE_G[1]);
      }
      if (lit) FLICK.push({ x: x - 1, y: y - hgt + 3 + Math.floor(r() * 3), k: 24 + Math.floor(r() * 50), ph: r(), col: TREE_G[4] });
    } else {
      const rx = 3 + Math.floor(r() * 4), ry = 2 + Math.floor(r() * 3);
      for (let j = -ry; j <= ry; j++) for (let i = -rx; i <= rx; i++) {
        const dd = (i * i) / (rx * rx + 1) + (j * j) / (ry * ry + 1);
        if (dd > 1 - hash(x + i, y + j, 41) * 0.35) continue;
        const v = (lit ? 0.55 : 0.3) - (i + j * 1.4) / (rx + ry) * 0.45 + (hash(x + i, y + j, 42) - 0.5) * 0.3;
        put(x + i, y - ry + j, pick(TREE_G, v, x + i, y + j));
        if (lit && j < 0 && i < 1 && hash(x + i, y + j, 43) < 0.16) FLICK.push({ x: x + i, y: y - ry + j, k: 24 + Math.floor(r() * 50), ph: r(), col: TREE_G[4] });
      }
    }
  }
  // Anafiotika: the islanders' whitewashed cubes under the north-east cliff
  for (let k = 0; k < 10; k++) {
    const x = 100 + k * 5 + Math.floor(r() * 3), w = 5 + Math.floor(r() * 3), hgt = 4 + Math.floor(r() * 2);
    const y = Math.round(Math.max(rockTop(x) + 8, 118 + r() * 12 - k * 0.6));
    for (let j = 0; j < hgt; j++) for (let i = 0; i < w; i++) put(x + i, y + j, j === 0 ? '#c4c2d2' : i === w - 1 ? '#6e6c8a' : '#9a98b2');
    const wx = x + 1 + Math.floor(r() * (w - 3)), lit = r() < 0.5;
    put(wx, y + 2, lit ? '#ffcf86' : '#2a2a48'); put(x + w - 2, y + hgt - 1, '#3a5a9a'); put(x + w - 2, y + hgt - 2, '#3a5a9a');
    if (!lit) ANA_WIN.push({ x: wx, y: y + 2, n: 3 + Math.floor(r() * 4), off: r(), id: k });
  }
  for (const l of ROCK_LAMPS) { put(l.x, l.y, '#fff6d8'); put(l.x + 1, l.y, '#e8c888'); }
  return p.done();
}
// bats hunting in the floodlights: [cx, cy, ax, ay, ka, kb, pa, pb]
const BATS = [[168, 84, 26, 9, 24, 36, 0, 0.2], [232, 88, 30, 8, 36, 24, 0.3, 0.6], [300, 86, 28, 9, 24, 48, 0.55, 0.1],
  [356, 82, 22, 10, 36, 48, 0.8, 0.4], [262, 52, 40, 9, 12, 36, 0.15, 0.7], [208, 94, 20, 7, 48, 36, 0.4, 0.9]];
function drawRockFx(t) {
  for (const f of FLICK) if (wave(t, f.k, f.ph) > 0.25) px(ctx, f.x, f.y, f.col);
  for (const w of ANA_WIN) if (hash(w.id + 20, Math.floor(frac(t / LOOP + w.off) * w.n), 3) < 0.5) px(ctx, w.x, w.y, '#ffcf86');
  for (const l of ROCK_LAMPS) halo(HL.rockLamp, l.x, l.y, 0.85 + 0.1 * wave(t, 16, l.x * 0.01));
  halo(HL.temple, 244, 50, 0.9 + 0.06 * wave(t, 6, 0.2)); halo(HL.templeS, ERE.porch + 5, ERE.top, 0.6); halo(HL.templeS, PROP.x0 + 14, 62, 0.7);
  const fl = step(t, 1920);
  ctx.fillStyle = '#1c1420';
  BATS.forEach(([cx, cy, ax, ay, ka, kb, pa, pb], i) => {
    const x = Math.round(cx + ax * Math.sin(TAU * (ka * t / LOOP + pa)) + 3 * Math.sin(TAU * (96 * t / LOOP + pb)));
    const y = Math.round(cy + ay * Math.sin(TAU * (kb * t / LOOP + pb)) + 2 * Math.sin(TAU * (132 * t / LOOP + pa)));
    const up = (fl + i) & 1;
    ctx.fillRect(x, y, 1, 1); ctx.fillRect(x - 1, y + (up ? -1 : 1), 1, 1); ctx.fillRect(x + 1, y + (up ? -1 : 1), 1, 1);
  });
}

/* ================= Plaka: old houses climbing to the foot of the rock ================= */
const TWIN = [];                                      // windows that switch on and off: { id, x, y, w, h, n, off }
const TVWIN = [];                                     // a television flickering behind a curtain: { x, y }
const NIGHT = '#14142c';
const HOUSE_PAL = [['#3c3446', '#5a4a52', '#7a6460'], ['#40343c', '#64504a', '#8a7058'], ['#34364a', '#4c5064', '#6a7084'],
  ['#44323c', '#6c4a4c', '#94685e'], ['#3a3a40', '#5c5a56', '#807a6c']];
function house(p, x, w, roof, base, dim, r) {
  const P = HOUSE_PAL[Math.floor(r() * HOUSE_PAL.length)].map(c => tint(c, NIGHT, 1 - dim));
  const tile = [tint('#9a5a40', NIGHT, 1 - dim), tint('#70402f', NIGHT, 1 - dim), tint('#58302a', NIGHT, 1 - dim)];
  for (let i = x; i < x + w; i++) for (let y = roof; y < base; y++) {
    const k = y - roof;
    p.put(i, y, k === 0 ? tile[0] : k < 3 ? (bayer(i, y) < 0.5 ? tile[1] : tile[2]) : k === 3 ? P[2] : i === x ? P[2] : i === x + w - 1 ? P[0] : P[1]);
  }
  for (let wy = roof + 6; wy < base - 4; wy += 8) for (let wx = x + 2; wx < x + w - 3; wx += 4) {
    const v = r(), lit = v < 0.3;
    for (let j = 0; j < 4; j++) for (let i = 0; i < 2; i++) p.put(wx + i, wy + j, lit ? (j < 1 ? '#ffe0a0' : '#f4b060') : '#15142a');
    if (!lit && v < 0.52) TWIN.push({ id: TWIN.length, x: wx, y: wy, w: 2, h: 4, n: 3 + Math.floor(r() * 5), off: r() });
    else if (!lit && v < 0.6) TVWIN.push({ x: wx, y: wy });
  }
  if (r() < 0.3) { const cx = x + 2 + Math.floor(r() * (w - 4)); for (let y = roof - 3; y < roof; y++) p.put(cx, y, y === roof - 3 ? tile[0] : P[0]); }   // chimney
}
function drawPlaka(p, r) {
  for (const [base, top, jit, dim] of [[152, 121, 5, 0.5], [166, 134, 6, 0.72], [GROUND + 2, 148, 6, 0.9]]) {
    let x = -4 - Math.floor(r() * 8);
    while (x < W + 4) { const w = 11 + Math.floor(r() * 12); house(p, x, w, top + Math.floor(r() * jit), base, dim, r); x += w; }
  }
}

/* ================= A neoclassical block: cornice, tall shuttered windows, iron balconies ================= */
const FIGWIN = [];                                    // lit windows someone walks past: { x, y, w, h, k, ph }
function drawBlock(p, x0, x1, top, base, P, r, floors) {
  const put = p.put;
  for (let y = top; y < base; y++) for (let i = x0; i <= x1; i++) {
    let v = 0.3 + 0.45 * (y - top) / (base - top) + (hash(i, y, 51) - 0.5) * 0.08;
    if (i === x0) v += 0.2; else if (i === x1) v -= 0.25;
    put(i, y, pick(P, v, i, y));
  }
  for (let i = x0 - 1; i <= x1 + 1; i++) { put(i, top - 2, P[3]); put(i, top - 1, P[4]); put(i, top, P[1]); }   // cornice
  const fh = (base - top) / floors;
  for (let f = 0; f < floors; f++) {
    const fy = Math.round(top + f * fh), wy = fy + 5;
    for (let i = x0; i <= x1; i++) put(i, fy + Math.round(fh) - 1, P[1]);                                 // string course
    for (let wx = x0 + 6; wx + 6 <= x1 - 3; wx += 13) {
      const v = r(), lit = v < 0.45, hgt = Math.min(11, Math.round(fh) - 9);
      for (let j = 0; j < hgt; j++) for (let i = 0; i < 6; i++) {
        let col = lit ? (j < 3 ? '#ffe2a4' : '#f6b866') : (j < 3 && i < 2 ? '#26244a' : '#131226');
        if (i === 0 || i === 5) col = lit ? '#5e7a64' : '#2c3a3a';                                          // green shutters
        put(wx + i, wy + j, col);
      }
      for (let i = -1; i <= 6; i++) { put(wx + i, wy - 1, P[4]); put(wx + i, wy + hgt, '#1a1622'); if ((i & 1) === 0) { put(wx + i, wy + hgt - 1, '#1a1622'); put(wx + i, wy + hgt - 2, '#1a1622'); } }   // lintel, balcony rail
      put(wx - 1, wy + hgt - 3, '#1a1622'); put(wx + 6, wy + hgt - 3, '#1a1622');
      for (let i = -1; i <= 6; i++) put(wx + i, wy + hgt - 3, '#2a2432');
      if (lit && v < 0.25) FIGWIN.push({ x: wx + 1, y: wy + 2, w: 4, h: hgt - 5, k: 3 + Math.floor(r() * 5), ph: r() });
      else if (!lit && v < 0.68) TWIN.push({ id: TWIN.length, x: wx + 1, y: wy, w: 4, h: hgt - 3, n: 3 + Math.floor(r() * 4), off: r() });
    }
  }
}

/* ================= The Tzistarakis mosque (1759): a cube on a podium of shops, a tiled dome on an octagonal drum,
   the three-arched porch with its little domes on the west side, the minaret long gone ================= */
const MQ = ['#4a3a38', '#6a5244', '#8a6e52', '#ac8c62', '#ccac78', '#e8cc96'];
const DOME = ['#3e2220', '#5e2e24', '#84402c', '#a85636', '#c87244', '#e09258'];
const PORCH_ARCH = [MOSQ.x1 + 2, MOSQ.x1 + 9, MOSQ.x1 + 16];        // left edges of the porch's arches (6 px wide)
function drawMosque(p) {
  const put = p.put, { x0, x1, cx, top, drum, r: R, pod, px1 } = MOSQ;
  for (let y = drum - R; y <= drum; y++) {            // the dome
    const hw = Math.round(Math.sqrt(R * R - (drum - y) * (drum - y)));
    for (let i = -hw; i <= hw; i++) {
      const v = 0.62 - (i / R) * 0.42 + ((drum - y) / R) * 0.12 + (((i + 40) % 4 === 0) ? -0.12 : 0) + (hash(i, y, 55) - 0.5) * 0.1;
      put(cx + i, y, pick(DOME, v, cx + i, y));
    }
  }
  for (let y = drum - R - 5; y < drum - R; y++) put(cx, y, y < drum - R - 3 ? '#f0d078' : '#b8923c');   // finial
  for (let y = drum; y < top; y++) for (let i = cx - 18; i <= cx + 18; i++) {                        // octagonal drum
    const d = i - (cx - 18);
    let v = 0.55 + (d < 9 ? 0.2 : d > 27 ? -0.25 : 0) + (d === 9 || d === 27 ? -0.12 : 0);
    if (y === drum) v += 0.25; else if (y === top - 1) v -= 0.1;
    put(i, y, pick(MQ, v, i, y));
  }
  for (const wx of [cx - 13, cx - 1, cx + 12]) for (let y = drum + 3; y < drum + 8; y++) { put(wx, y, '#1c1a30'); put(wx + 1, y, y === drum + 3 ? '#6a5244' : '#26233c'); }
  for (let y = top; y < pod; y++) for (let i = x0; i <= x1; i++) {                                   // the prayer hall
    let v = 0.42 + 0.3 * (y - top) / (pod - top) + (vnoise(i / 5, y / 3, 57) - 0.5) * 0.22;
    if (i <= x0 + 1) v += 0.2; else if (i >= x1 - 1) v -= 0.22;
    if (y <= top + 1) v += y === top ? 0.3 : -0.15;
    if ((y - top) % 5 === 4) v -= 0.06;
    put(i, y, pick(MQ, v, i, y));
  }
  for (const wx of [x0 + 7, x0 + 19, x0 + 31, x0 + 43]) {                                             // two tiers of windows
    for (let y = top + 6; y < top + 10; y++) for (let i = 0; i < 3; i++) put(wx + i, y, y === top + 6 && i !== 1 ? MQ[2] : '#1c1a30');
    for (let y = top + 16; y < top + 26; y++) for (let i = 0; i < 4; i++) {
      const arch = y === top + 16 && (i === 0 || i === 3), lit = wx === x0 + 19;
      put(wx + i, y, arch ? MQ[2] : lit ? (y < top + 19 ? '#ffe2a4' : '#f0b060') : (i + y) & 1 ? '#1c1a30' : '#2a2844');
    }
    for (let i = -1; i <= 4; i++) put(wx + i, top + 26, MQ[5]);
  }
  // the porch: marble columns, three pointed arches, a little dome over each bay, the door glowing inside
  for (let y = top + 8; y < pod; y++) for (let i = x1 + 1; i <= px1; i++) put(i, y, y < top + 12 ? pick(MQ, 0.6 - (i - x1) * 0.012, i, y) : '#3a2c2c');
  for (const ax of PORCH_ARCH) {
    for (let k = 0; k < 4; k++) { const hw = [1, 2, 3, 3][k]; for (let i = -hw; i <= hw; i++) put(ax + 3 + i, top + 3 + k, pick(DOME, 0.6 - i * 0.12, ax + i, top + k)); }
    for (let y = top + 12; y < pod - 2; y++) for (let i = 0; i < 6; i++) {
      const ly = y - top - 12, open = ly > 1 || (ly === 1 ? i > 0 && i < 5 : i > 1 && i < 4);
      put(ax + i, y, open ? (y > top + 20 && i > 1 && i < 4 && ax === PORCH_ARCH[1] ? '#f0b060' : '#2a2030') : MQ[4]);
    }
  }
  for (const cx2 of [x1 + 1, x1 + 8, x1 + 15, x1 + 22]) for (let y = top + 14; y < pod; y++) put(cx2, y, y === top + 14 ? '#fff4dc' : '#e8dcc0');
  for (let i = x1 + 1; i <= px1; i++) { put(i, top + 7, MQ[5]); put(i, pod - 2, '#d8c8a8'); put(i, pod - 1, '#8a7a66'); }
  // the podium: rough stone, two arched shops full of pottery, the stair up to the porch
  for (let y = pod; y <= GROUND; y++) for (let i = x0 - 2; i <= px1; i++) {
    let v = 0.3 + 0.3 * (y - pod) / 16 + (vnoise(i / 3, y / 2, 58) - 0.5) * 0.35;
    if (y === pod) v = 0.95; else if (y === pod + 1) v = 0.2;
    put(i, y, pick(MQ, v, i, y));
  }
  for (const sx of [x0 + 6, x0 + 28]) for (let y = pod + 4; y <= GROUND; y++) for (let i = 0; i < 14; i++) {
    const ly = y - pod - 4;
    if (ly === 0 && (i < 3 || i > 10)) continue;
    if (ly === 1 && (i < 1 || i > 12)) continue;
    let col = ly < 4 ? '#ffe6ac' : ly < 8 ? '#f8c06c' : '#e09a4c';
    if (ly > 3 && ly < 10 && i > 1 && i < 12 && (i + ly) % 3 === 0) col = ['#3a6ab0', '#e8e4d8', '#c8503a', '#3a8a6a'][(i + ly * 2 + sx) % 4];   // jugs and plates
    put(sx + i, y, col);
  }
  for (let k = 0; k < 9; k++) for (let i = 0; i <= 22 - k * 2; i++) {                                   // the stair, rising to the left
    const sx = px1 - i - k * 2 + 20, sy = GROUND - k * 2;
    put(sx, sy, '#d8c8a8'); put(sx, sy - 1, '#a89878');
  }
}

/* ================= Areos street: the stair to the porch, Hadrian's Library receding on its east side ================= */
// the Library's west wall: Corinthian columns of green-veined marble on pedestals, each carrying its own block of
// entablature, seen down the street: [x, height]
const LIB_COLS = [[177, 23], [185, 20], [192, 17], [198, 15], [203, 13], [207, 11], [210, 10]];
const STRINGS = [];                                   // bulbs on the light strings: { x, y, ph, c }
function strand(p, ax, ay, bx, by, sag, every, c) {   // a sagging string of bulbs from a to b
  let prev = null;
  for (let x = ax; x <= bx; x++) {
    const f = (x - ax) / (bx - ax), y = Math.round(lerp(ay, by, f) + sag * Math.sin(Math.PI * f));
    if (prev !== null) for (let yy = Math.min(prev, y); yy <= Math.max(prev, y); yy++) p.put(x, yy, '#1c1826'); else p.put(x, y, '#1c1826');
    if ((x - ax) % every === 1) STRINGS.push({ x, y: y + 1, ph: hash(x, ay, 61), c });
    prev = y;
  }
}
function drawAreos(p) {
  const put = p.put, sx0 = MOSQ.px1;
  for (let y = 176; y <= GROUND; y++) for (let i = Math.round(lerp(206, 172, (y - 176) / 10)); i < STN.x0; i++)   // the street, climbing south
    put(i, y, pick(['#4e4048', '#665250', '#806658', '#9a7c62'], 0.75 - (GROUND - y) * 0.05 + (hash(i >> 1, y, 63) - 0.5) * 0.3, i, y));
  for (let i = 172; i <= 212; i++) {                  // the Library's wall behind the columns, lit from below
    const top = Math.round(lerp(160, 175, (i - 172) / 40)), foot = Math.round(lerp(GROUND, 177, (i - 172) / 40));
    for (let y = top; y <= foot; y++) put(i, y, y === top ? '#b89a70' : pick(['#3e3038', '#5a4640', '#7a604c', '#987a58'], 0.2 + 0.7 * (y - top) / (foot - top + 1) + (vnoise(i / 3, y / 2, 64) - 0.5) * 0.3, i, y));
  }
  LIB_COLS.forEach(([cx, hgt], k) => {
    const foot = Math.round(GROUND - k * 1.5), top = foot - hgt, wide = k < 4;
    for (let y = top; y <= foot; y++) {
      const ly = y - top;
      if (ly < 2) { for (let i = -1; i <= (wide ? 2 : 1); i++) put(cx + i, y, ly === 0 ? '#f4ecd0' : '#c8c4a4'); continue; }      // its block of entablature
      if (ly === 2 || y >= foot - 1) { for (let i = -1; i <= (wide ? 2 : 1); i++) put(cx + i, y, y >= foot - 1 ? '#b0a888' : '#fff8dc'); continue; }   // capital, pedestal
      put(cx, y, '#dfe2c4'); if (wide) put(cx + 1, y, (ly & 3) === 0 ? '#8e9a84' : '#a8b094');
    }
  });
  for (let i = 1; i <= 20; i++) {                     // the stair from the square up to the porch
    const k = (20 - i) >> 1, ty = GROUND - 2 * k - 1;
    for (let y = ty; y <= GROUND; y++) put(sx0 + i, y, y === ty ? '#e6d8ba' : (y - ty) & 1 ? '#8a7a66' : '#a89878');
  }
  strand(p, 174, 158, STN.x0 + 2, 157, 5, 4, 0); strand(p, 190, 166, STN.x0, 166, 3, 3, 1);
}

/* ================= The station (1895): ochre walls, white pilasters, arched doors, a tiled roof ================= */
const OCHRE = ['#5a4428', '#7e6032', '#a6823e', '#cca44e', '#e6c268', '#f8dc8a'];
const TILE = ['#4a2622', '#6a3628', '#8e4a30', '#b0623a', '#cc7e4a'];
function drawStation(p) {
  const put = p.put, { x0, x1, ridge, eave, doors } = STN, mid = doors[2];
  for (let y = ridge; y < eave; y++) for (let i = x0 - 2 + (eave - 1 - y); i <= x1 + 2 - (eave - 1 - y); i++)   // hipped roof
    put(i, y, y === ridge ? TILE[4] : pick(TILE, 0.7 - (y - ridge) * 0.04 - ((i % 3) === 0 ? 0.25 : 0) + (i < mid ? 0.08 : -0.08), i, y));
  for (let y = eave; y <= GROUND; y++) for (let i = x0; i <= x1; i++) {
    let v = 0.5 + 0.3 * (y - eave) / (GROUND - eave) + (hash(i, y, 66) - 0.5) * 0.06;
    if (y === eave) v = 1.2; else if (y === eave + 1) v = 0.25; else if (y >= GROUND - 1) v -= 0.25;
    put(i, y, pick(OCHRE, v, i, y));
  }
  for (let y = ridge - 4; y <= eave + 1; y++) {       // the pedimented centre bay
    const hw = Math.round((y - (ridge - 4)) * 1.15) + 1;
    for (let i = mid - hw; i <= mid + hw; i++) if (hw <= 17) put(i, y, Math.abs(i - mid) >= hw - 1 || y === eave + 1 ? '#fff4d8' : pick(OCHRE, 0.62, i, y));
  }
  for (const px0 of [x0, x0 + 1, 239, 240, mid - 15, mid - 14, mid + 14, mid + 15, 320, 321, x1 - 1, x1])
    for (let y = eave + 2; y <= GROUND; y++) put(px0, y, y < eave + 4 ? '#fff8e4' : '#f0e2c0');             // pilasters
  doors.forEach((dx, k) => {                            // arched openings: the three in the middle are doors, bright inside
    const door = k > 0 && k < 4;
    for (let y = eave + 5; y <= GROUND; y++) for (let i = -6; i <= 6; i++) {
      const ly = y - eave - 5, hw = ly === 0 ? 3 : ly === 1 ? 5 : 6;
      if (Math.abs(i) > hw) continue;
      let col;
      if (Math.abs(i) === hw && ly < 3 || ly === 0) col = '#fff4d8';
      else if (door) col = ly < 6 ? '#fff6d0' : ly < 12 ? '#ffe2a0' : '#f2c070';
      else col = (i + 6) % 4 === 0 || ly === 8 ? '#3a3040' : ly < 8 ? '#f6d890' : '#d8a858';
      put(dx + i, y, col);
    }
  });
  for (let y = ridge + 2; y <= ridge + 8; y++) for (let i = mid - 11; i <= mid + 11; i++) put(i, y, y === ridge + 2 || y === ridge + 8 ? '#fff4d8' : '#1e3a6e');   // the name plate
}

/* ================= Ifestou, the flea-market lane: awnings, sandals, brass, coloured glass lamps ================= */
const LANE_LAMPS = [];                                // hanging mosaic lamps: { x, y, col, ph }
function drawLane(p, r) {
  const put = p.put, { x0, x1 } = LANE;
  for (let y = 150; y <= GROUND; y++) for (let i = x0; i <= x1; i++) put(i, y, y > 178 ? pick(['#4e4048', '#665250', '#806658'], 0.5 + (hash(i >> 1, y, 68) - 0.5) * 0.4, i, y) : pick(['#1c1828', '#2a2230', '#3a2c34'], 0.3 + 0.5 * (y - 150) / 30 + (Math.abs(i - (x0 + x1) / 2) / 20) * 0.5, i, y));
  for (const [sx, w, cols] of [[x0, 17, ['#c8483a', '#f0e6d0']], [x1 - 16, 17, ['#3a6aa8', '#f0e6d0']]]) {
    for (let y = 164; y < 169; y++) for (let i = 0; i < w; i++) put(sx + i, y, y === 168 ? ((i & 1) ? cols[0] : NIGHT) : cols[(i >> 1) & 1]);   // striped awning
    for (let y = 169; y <= 184; y++) for (let i = 0; i < w; i++) put(sx + i, y, y < 172 ? '#f6c878' : y < 180 ? '#e8a858' : '#b87a40');           // the lit stall
    for (let i = 0; i < w; i++) put(sx + i, 180, '#5a3a22');
  }
  for (let i = 1; i < 16; i += 3) { stamp(p, ['b.b', '.b.'], { b: i % 2 ? '#6a3a1e' : '#8a5a2e' }, x0 + i, 171); stamp(p, ['b.b', '.b.'], { b: '#7a4a26' }, x0 + i, 175); }   // sandals on strings
  for (let i = 1; i < 16; i += 2) { put(x1 - 16 + i, 178, r() < 0.5 ? '#f8d868' : '#d8a838'); if (r() < 0.6) put(x1 - 16 + i, 177, '#fff0a0'); }                               // brass pots and trays
  for (let y = 170; y < 177; y++) for (let i = 0; i < 5; i++) put(x1 - 7 + i, y, ['#a8322a', '#e8d8b0', '#2a4a8a'][(y + (i >> 1)) % 3]);                                    // a rug
  for (let k = 0; k < 7; k++) {
    const x = x0 + 4 + k * 5 + Math.floor(r() * 2), y = 157 + Math.round(3 * Math.sin(k * 1.3)) + Math.floor(r() * 3);
    for (let yy = 151; yy < y; yy++) put(x, yy, '#3a3040');
    LANE_LAMPS.push({ x, y, col: ['#ff8a4a', '#4ad0c8', '#ffd24a', '#e85ab0', '#6a9aff', '#8ae06a', '#ff5a4a'][k], ph: r() });
  }
}

/* ================= The taverna block: souvlaki at the corner, a terrace on the roof ================= */
const CREAM = ['#3a3040', '#564650', '#786260', '#9a8070', '#bca084', '#dcc29c'];
const ROSE = ['#3a2c3a', '#5a4048', '#7e5a58', '#a27868', '#c49a7e', '#e2bc98'];
function drawTaverna(p, r) {
  const put = p.put, x0 = BLOCK.x0, top = BLOCK.top, gy = 164;
  drawBlock(p, x0, W + 1, top, gy, CREAM, r, 2);
  for (let y = gy; y <= GROUND; y++) for (let i = x0; i < W; i++) put(i, y, y < gy + 7 ? (y === gy || y === gy + 6 ? '#0e2a22' : '#1c4a3a') : pick(CREAM, 0.5 + (y - gy) * 0.02, i, y));
  for (let y = gy + 8; y <= GROUND - 1; y++) for (let i = 0; i < SOUV.w; i++) {                       // the souvlaki window
    const ly = y - gy - 8, edge = i === 0 || i === SOUV.w - 1 || ly === 0;
    put(SOUV.x + i, y, edge ? '#2a1c16' : ly > 9 ? (ly === 10 ? '#e8e0d0' : '#8a8a94') : ly < 4 ? '#fff0c0' : '#ffd890');
  }
  for (let y = gy + 8; y <= GROUND; y++) for (let i = 0; i < 11; i++) put(430 + i, y, i === 0 || i === 10 || y === gy + 8 ? '#2a1c16' : y < gy + 13 ? '#ffe2a4' : '#f0b060');   // the door
  for (let y = gy + 9; y <= GROUND - 3; y++) for (let i = 446; i < W - 1; i++) {                      // the dining room's windows, red check curtains
    const ly = y - gy - 9, bar = (i - 446) % 11 === 0;
    put(i, y, bar ? '#2a1c16' : ly < 3 ? (((i >> 1) + ly) & 1 ? '#c83a30' : '#f4ece0') : ly < 8 ? '#ffd890' : '#e8a858');
  }
  // the roof terrace: a glass rail, two parasols, a string of bulbs
  for (let i = x0; i < W; i++) { put(i, top - 9, '#c8c0b0'); for (let y = top - 8; y < top - 2; y++) put(i, y, (i - x0) % 12 === 0 ? '#8a8478' : bayer(i, y) < 0.3 ? '#3a4460' : '#2a3250'); }
  for (const ux of [x0 + 24, x0 + 68]) {
    for (let k = 0; k < 5; k++) for (let i = -2 - k * 2; i <= 2 + k * 2; i++) put(ux + i, top - 26 + k, k === 4 ? '#c8bca0' : i < 0 ? '#f4ecd8' : '#d8ccb0');
    for (let y = top - 21; y < top - 9; y++) put(ux, y, '#6a6258');
  }
  strand(p, x0 + 2, top - 30, x0 + 46, top - 31, 5, 4, 0); strand(p, x0 + 46, top - 31, W - 2, top - 29, 5, 4, 0);
  for (const sx of [x0 + 2, x0 + 46, W - 2]) for (let y = top - 31; y < top - 9; y++) put(sx, y, '#3a3440');
}
function drawLeftHouse(p, r) {
  drawBlock(p, -2, LEFTB.x1, LEFTB.top, GROUND + 1, ROSE, r, 3);
  const top = LEFTB.top;
  for (let i = 0; i <= LEFTB.x1; i++) { p.put(i, top - 9, '#c8c0b0'); for (let y = top - 8; y < top - 2; y++) p.put(i, y, i % 12 === 3 ? '#8a8478' : bayer(i, y) < 0.3 ? '#3a4460' : '#2a3250'); }
  strand(p, 2, top - 26, LEFTB.x1 - 2, top - 27, 5, 4, 0);
  for (const sx of [2, LEFTB.x1 - 2]) for (let y = top - 27; y < top - 9; y++) p.put(sx, y, '#3a3440');
  for (let y = 170; y <= GROUND; y++) for (let i = 54; i <= 64; i++) p.put(i, y, y === 170 || i === 54 || i === 64 ? '#2a1c16' : y < 175 ? '#ffe2a4' : '#f0b060');   // the kafeneio's door
}

/* ================= The square's paving: a patchwork of marble and stone in flowing bands, warm pools of light ================= */
const LAMPS = [{ x: 132, base: 203, head: 149 }, { x: 350, base: 203, head: 147 }];      // twin-lantern street lamps
const TREES = [{ x: 214, base: 201, r: 10, seed: 3 }, { x: 438, base: 200, r: 8, seed: 5 }, { x: 68, base: 204, r: 10, seed: 8 }];
// warm light on the stones: [cx, cy, rx, ry, colour, strength]
const DUO_POOL = [DUO.x + 4, DUO.y + 3, 30, 9, '#ffb060', 0.45];     // the light of the duo's lantern: theirs, it goes with them
const POOLS = [
  [132, 207, 50, 13, '#ffc070', 0.6], [350, 207, 50, 13, '#ffc070', 0.6], ...STN.doors.slice(1, 4).map(d => [d, 191, 13, 6, '#ffe0a0', 0.75]),
  [SOUV.x + 14, 191, 24, 7, '#ffd088', 0.7], [436, 192, 12, 6, '#ffc070', 0.6], [464, 196, 30, 10, '#ffc070', 0.5],
  [CART.x + 16, CART.y + 3, 30, 9, '#ffd088', 0.6], [98, 190, 34, 5, '#ffc070', 0.6], DUO_POOL,
  [366, 190, 22, 5, '#ffb070', 0.6], [194, 189, 22, 4, '#ffc070', 0.5], [59, 190, 10, 4, '#ffc070', 0.6],
];
function pooled(i, y, col, pools = POOLS) {
  for (const [cx, cy, rx, ry, lc, s] of pools) {
    const d = Math.hypot((i - cx) / rx, (y - cy) / ry);
    if (d >= 1) continue;
    const q = Math.floor((1 - d) * s * 5 + bayer(i, y)) / 5;
    if (q > 0) col = tint(col, lc, Math.min(0.6, q * 0.7));
  }
  return col;
}
const PAVE = ['#2c2536', '#3a2f3c', '#4a3b43', '#5c4a4b', '#705a53', '#846a5a', '#9a7e64'];
// p.put takes each stone's colour; the other arguments paint a part of the paving under another set of pools
function drawGround(p, pools = POOLS, x0 = 0, x1 = W, y0 = GROUND + 1, y1 = H) {
  for (let y = y0; y < y1; y++) {
    const d = y - GROUND, sw = 3 + d * 0.09, sh = d < 12 ? 1 : d < 38 ? 2 : 3, row = Math.floor(d / sh);
    const off = (row & 1) * sw * 0.5 + 36 * Math.sin(row * 0.29) + 400;
    for (let i = x0; i < x1; i++) {
      const cell = Math.floor((i + off) / sw), h2 = hash(cell, row, 72);
      const v = 0.52 + 0.34 * (hash(cell, row, 71) - 0.5) + 0.1 * Math.sin(cell * 0.23 + row * 0.37) - 0.16 * d / 84;
      let col = PAVE[clamp(Math.round(v * 6), 0, 6)];
      if (h2 < 0.05) col = '#28263a'; else if (h2 < 0.09) col = '#6a4236'; else if (h2 < 0.12) col = '#948a7e';
      if (sw >= 4.5 && Math.floor((i + 1 + off) / sw) !== cell) col = darker(col, 0.3);              // joints, where the stones are big enough
      else if (sh >= 2 && d % sh === 0) col = darker(col, 0.22);
      p.put(i, y, pooled(i, y, col, pools));
    }
  }
  if (y0 > GROUND + 4) return;                         // a part of the paving below the station's steps
  for (let i = STN.x0 - 2; i <= STN.x1 + 2; i++) { p.put(i, GROUND + 1, '#e6d8b4'); p.put(i, GROUND + 2, '#8a7a66'); p.put(i, GROUND + 3, '#c2b08c'); p.put(i, GROUND + 4, '#6a5c50'); }   // the station's steps
}
// crowd 0: the stones where the duo plays, without the pool of their lantern (drawn over the town at that level)
const BARE = { x: DUO_POOL[0] - DUO_POOL[2], y: DUO_POOL[1] - DUO_POOL[3], w: DUO_POOL[2] * 2 + 1, h: DUO_POOL[3] * 2 + 1 };
function renderBare() {
  const p = painter(BARE.w, BARE.h);
  drawGround({ put: (i, y, col) => p.put(i - BARE.x, y - BARE.y, col) }, POOLS.filter(P => P !== DUO_POOL), BARE.x, BARE.x + BARE.w, BARE.y, BARE.y + BARE.h);
  return p.done();
}
function renderTown() {
  const p = painter(W, H), r = rng(808);
  drawPlaka(p, r); drawLeftHouse(p, r); drawAreos(p); drawMosque(p); drawStation(p); drawLane(p, r); drawTaverna(p, r); drawGround(p);
  // a window that a nearer building has since covered must not light up on its wall
  for (let k = TWIN.length - 1; k >= 0; k--) { const w = TWIN[k], c = p.rgb(w.x, w.y + w.h - 1); if (c !== 0x15142a && c !== 0x131226) TWIN.splice(k, 1); }
  for (let k = TVWIN.length - 1; k >= 0; k--) if (p.rgb(TVWIN[k].x, TVWIN[k].y + 3) !== 0x15142a) TVWIN.splice(k, 1);
  for (let k = FIGWIN.length - 1; k >= 0; k--) { const w = FIGWIN[k]; if (p.rgb(w.x, w.y + w.h - 1) !== 0xf6b866) FIGWIN.splice(k, 1); }
  FIGWIN.forEach((w, k) => { w.rank = spread(k); });                 // crowd: the figures in the windows thin too
  const c = p.done();
  glyph(p.x, ['10001', '11011', '10101', '10001', '10001'], STN.doors[2] - 10, STN.ridge + 3, '#f4f0e0'); greek(p.x, 'ETPO', STN.doors[2] - 4, STN.ridge + 3, '#f4f0e0');
  greek(p.x, 'SOYBLAKI', SOUV.x + 1, 165, '#ffe9a0'); greek(p.x, 'TABEPNA', 448, 165, '#ffe9a0');
  return c;
}

/* ================= The church of the Pantanassa (left foreground): stone framed in brick, a tiled gable, a little belfry ================= */
const CHC = { y: 104 };                               // top row of its canvas (60 px wide, from x = 0)
const STONE = ['#4a4048', '#665a58', '#847668', '#a29278', '#bcaa88'];
const churchTop = x => CH.top + Math.abs(x - CH.apex) * (CH.eave - CH.top) / CH.apex;
function renderChurch() {
  const p = painter(60, H - CHC.y), put = (i, y, col) => p.put(i, y - CHC.y, col);
  for (let i = 0; i <= CH.x1; i++) {
    const top = Math.round(churchTop(i));
    for (let y = top; y <= CH.foot; y++) {
      const k = y - top;
      let col;
      if (k < 2) col = k === 0 ? '#c87a4a' : '#8e4a30';                                       // tiles along the gable
      else if (k === 2) col = (i & 1) ? '#b0623a' : '#3a2c30';                               // a dog-tooth course of brick
      else {
        const course = (y % 5) === 0, joint = ((i + (Math.floor(y / 5) & 1) * 4) % 8) === 0;
        if (course || joint) col = (hash(i, y, 81) < 0.8) ? (i > 30 ? '#a65a3c' : '#84442f') : '#6a5a58';   // brick round each block
        else col = pick(STONE, 0.3 + 0.5 * i / CH.x1 + (hash(i >> 3, Math.floor(y / 5), 82) - 0.5) * 0.3 - (y > 228 ? 0.15 : 0), i, y);
      }
      if (i === CH.x1) col = '#3a3038';
      put(i, y, col);
    }
  }
  for (let i = -1; i <= CH.x1 + 2; i++) { put(i, CH.foot - 1, '#c2b29a'); put(i, CH.foot, '#7e7062'); }   // the step
  // the door: a marble frame, an arch of brick and stone, the dark nave behind (its light is drawn per frame)
  const { x: dx, w: dw, y: dy } = CH.door, dc = dx + dw / 2;
  for (let y = dy - 9; y < CH.foot - 1; y++) for (let i = dx - 2; i < dx + dw + 2; i++) {
    const ay = dy - y, r2 = Math.hypot(i + 0.5 - dc, Math.max(0, ay));
    if (ay > 0 && r2 > dw / 2 + 2.2) continue;
    const inside = i >= dx && i < dx + dw && (ay <= 0 || r2 < dw / 2);
    put(i, y, inside ? '#1a1420' : ay > 0 ? ((Math.floor(Math.atan2(ay, i + 0.5 - dc) * 2.6 + 0.4) & 1) ? '#9a5238' : '#cdbfa4') : i < dx ? '#cdbfa4' : '#8e8272');
  }
  // the icon in its niche over the door, the double window in the gable
  for (let y = 0; y < 7; y++) for (let i = 0; i < 5; i++) if (y > 0 || (i > 0 && i < 4)) put(dc - 2 + i, dy - 21 + y, y > 1 && i > 0 && i < 4 ? (y < 4 ? '#e8b848' : '#8a3a2a') : '#d8ccb4');
  for (const wx of [dc - 5, dc + 1]) for (let y = 0; y < 9; y++) for (let i = 0; i < 4; i++) if (y > 0 || (i > 0 && i < 3)) put(wx + i, 168 + y, '#1a1420');
  for (let y = 169; y < 177; y++) put(dc, y, '#e8dcc0');
  // the belfry: two piers, an arch, a tiled cap, the cross
  const bx = CH.apex;
  for (let y = BELL.y - 10; y <= CH.top + 2; y++) for (let i = -8; i <= 8; i++) {
    const ly = y - (BELL.y - 10), open = Math.abs(i) < 5 && ly > 2 + (Math.abs(i) > 3 ? 2 : Math.abs(i) > 1 ? 1 : 0) && y < CH.top - 2;
    if (!open) put(bx + i, y, (y % 4) === 0 ? '#84442f' : pick(STONE, 0.55 + i * 0.04, bx + i, y));
  }
  for (let k = 0; k < 5; k++) for (let i = -10 + k * 2; i <= 10 - k * 2; i++) put(bx + i, BELL.y - 11 - k, k === 4 || Math.abs(i) === 10 - k * 2 ? '#c87a4a' : (i & 1) ? '#8e4a30' : '#a65a3c');
  for (let y = BELL.y - 24; y < BELL.y - 15; y++) put(bx, y, '#e8d090');
  for (let i = -2; i <= 2; i++) put(bx + i, BELL.y - 21, '#e8d090');
  return p.done();
}

/* ================= Bitter-orange trees in blossom, street lamps ================= */
const LEAF = ['#0e2416', '#16361e', '#22502a', '#327038', '#4a8a48'];
const BLOSSOMS = [];                                  // blossom clusters that catch the light: { x, y, k, ph }
function renderTree(T) {
  const R = T.r, w = R * 2 + 5, h = R * 2 + 30, p = painter(w, h), r = rng(900 + T.seed), cx = R + 2, cy = R + 1;
  for (let y = cy + R - 3; y < h; y++) { p.put(cx, y, '#3a2a22'); p.put(cx + 1, y, '#241a18'); }                // trunk
  for (let i = -3; i <= 4; i++) { p.put(cx + i, h - 1, '#2a2430'); if (Math.abs(i - 0.5) < 3) p.put(cx + i, h - 2, '#1c1822'); }   // the grate
  for (let y = -R; y <= R; y++) for (let i = -R - 1; i <= R + 1; i++) {
    const d = Math.hypot(i / (R + 1), y / (R * 0.86));
    if (d > 1 - hash(i, y, 90 + T.seed) * 0.3) continue;
    const v = 0.45 - (i * 0.5 + y) / (R * 2) * 0.55 + (hash(i >> 1, y >> 1, 91 + T.seed) - 0.5) * 0.5;
    p.put(cx + i, cy + y, pick(LEAF, v, i, y));
  }
  for (let k = 0; k < R * 5; k++) {                   // oranges and clusters of white blossom
    const a = r() * TAU, d = Math.sqrt(r()) * (R - 2), x = Math.round(cx + Math.cos(a) * d), y = Math.round(cy + Math.sin(a) * d * 0.8);
    if (k % 3 === 0) { p.put(x, y, '#ff9a2a'); p.put(x + 1, y, '#e0701a'); p.put(x, y + 1, '#c85a18'); p.put(x + 1, y + 1, '#a84a16'); }
    else { p.put(x, y, '#fff8ec'); if (r() < 0.5) p.put(x + 1, y, '#e8dcc8'); BLOSSOMS.push({ x: T.x - cx + x, y: T.base - h + 1 + y, k: 20 + Math.floor(r() * 40), ph: r() }); }
  }
  return p.done();
}
function renderLamp() {                               // 13 x 58: a post, a crossbar, two lanterns
  const p = painter(13, 58);
  for (let y = 5; y < 58; y++) { p.put(6, y, y > 50 ? '#4a4450' : '#2e2a38'); if (y > 50) { p.put(5, y, '#2e2a38'); p.put(7, y, '#1c1a24'); } }
  for (let i = 1; i <= 11; i++) p.put(i, 5, '#3a3644');
  p.put(6, 4, '#3a3644'); p.put(6, 3, '#4a4450');
  for (const lx of [0, 10]) { for (let y = 6; y < 11; y++) for (let i = 0; i < 3; i++) p.put(lx + i, y, y === 6 || y === 10 ? '#2e2a38' : i === 1 ? '#fffbe4' : '#ffe2a0'); }
  return p.done();
}

/* ================= The fruit and nut cart, the koulouri seller's stand, taverna tables ================= */
function renderCart() {                               // 34 x 30: wheels, a tray of strawberries, cherries, coconut and nuts, a bulb on a pole
  const p = painter(34, 30), W2 = ['#3a2416', '#5a3a22', '#7e5430', '#a87440'];
  for (let y = 0; y < 18; y++) { p.put(17, y, '#4a4450'); }                                                    // the pole
  for (let i = 12; i <= 22; i++) p.put(i, 3, '#3a3644');
  for (let i = 0; i < 34; i++) for (let y = 18; y < 27; y++) {
    const ly = y - 18;
    let col = ly < 3 ? ['#d8303a', '#b01e30', '#f4f0e4', '#8a1830', '#c8843e', '#e84a4a', '#f4f0e4', '#6a3a1e'][(i >> 2) % 8] : ly === 3 ? W2[3] : (i % 5 === 4 ? W2[0] : ly < 7 ? W2[2] : W2[1]);
    if (ly < 3 && hash(i, y, 95) < 0.3) col = tint(col, '#ffffff', 0.35);
    if (ly === 0 && (i & 3) === 3) continue;
    p.put(i, y, col);
  }
  for (const wx of [5, 27]) stamp(p, ['.kkk.', 'kmmmk', 'kmhmk', 'kmmmk', '.kkk.'], { k: '#14121a', m: '#5a5660', h: '#8a8690' }, wx - 2, 25);
  return p.done();
}
function renderStand() {                              // 13 x 16: a glass case of sesame rings on a trolley
  const p = painter(13, 16);
  for (let y = 0; y < 10; y++) for (let i = 0; i < 13; i++) {
    const edge = y === 0 || i === 0 || i === 12 || y === 9;
    p.put(i, y, edge ? '#c8c0b0' : (y % 3 === 1 && i % 3 !== 0) ? '#d89a48' : (y % 3 === 2 && i % 3 === 1) ? '#a86a2a' : '#ffe6b0');
  }
  for (let y = 10; y < 15; y++) { p.put(2, y, '#4a4450'); p.put(10, y, '#4a4450'); }
  p.put(1, 15, '#14121a'); p.put(2, 15, '#14121a'); p.put(10, 15, '#14121a'); p.put(11, 15, '#14121a');
  return p.done();
}
const TABLE_ROWS = ['.wwwwwwwww.', 'cccccccccc.', '....kk.....', '....kk.....', '....kk.....', '....kk.....', '...kkkk....'];
const CHAIR_ROWS = ['b...', 'b...', 'b...', 'bbbb', 'b..b', 'b..b'];

/* ================= People ================= */
// A figure is built from a few measurements that follow from its height, so the same person can stand at any depth of
// the square. put(i, j, col): i counted from the centre column, j from the feet row (0), negative upwards.
// S: { h, coat, legs, skin, hair, hat: 'cap' | 'sun' | 'kal', hatCol, long, longHair, bag, pack, scarf, beard }
// pose: view 0 side (facing right) / 1 back / 2 front; stride -1 | 0 | 1; bob; arm 0 swing / 1 held out / 2 one up /
// 3 both up / 4 out to the sides / 5 hands together; sit; kick -1 | 0 | 1 (a leg lifted, front view)
const dims = h => { const hh = h >= 22 ? 5 : h >= 15 ? 4 : 3, th = Math.round(h * 0.36); return { hh, hw: h >= 20 ? 4 : 3, tw: h >= 22 ? 6 : h >= 15 ? 5 : 4, th, lh: h - hh - th }; };
const SHOE = '#0e0c12';
function drawFigure(put, S, { view = 0, stride = 0, bob = 0, arm = 0, sit = false, kick = 0 } = {}) {
  const { hh, hw, tw, th, lh } = dims(S.h), big = S.h >= 20, lw = tw >= 5 ? 2 : 1, x0 = -((tw - 1) >> 1);
  const coatD = darker(S.coat), legsD = darker(S.legs, 0.3), sh = Math.max(3, Math.round(lh * 0.55));
  const R = (i, j, w, h, col) => { for (let y = j; y < j + h; y++) for (let x = i; x < i + w; x++) put(x, y, col); };
  const tb = sit ? -(sh + 1) : -lh - bob, tt = tb - th + 1;       // torso: bottom and top rows
  if (sit) {                                                      // thighs forward, shins down
    R(x0 + tw - 2, -sh - 1, 4, 2, S.legs); R(x0 + tw + 1, -sh + 1, lw, sh - 1, S.legs); R(x0 + tw + 1, 0, lw + 1, 1, SHOE);
  } else if (view === 0) {
    const half = lh >> 1, ext = big && stride === 1 ? 1 : 0;
    for (const [bx, s, col] of [[lw === 2 ? -1 : 0, -stride, legsD], [1, stride, S.legs]]) {
      R(bx + s, -lh + 1 - bob, lw, lh - half + bob, col);
      R(bx + s + Math.sign(s) * ext, -half + 1, lw, half - 1, col);
      R(bx + s + Math.sign(s) * ext, 0, lw + 1, 1, SHOE);
    }
  } else {
    const xl = x0 + (tw >= 5 ? 0 : 1), xr = x0 + tw - lw - (tw >= 5 ? 0 : 1);
    for (const [bx, k, col] of [[xl, -1, view === 1 ? S.legs : legsD], [xr, 1, S.legs]]) {
      if (kick === k) { R(bx + k, -lh + 1 - bob, lw, lh - 3, col); R(bx + 2 * k, -3, lw, 1, col); R(bx + 2 * k, -2, lw, 1, SHOE); }
      else { R(bx, -lh + 1 - bob, lw, lh - 1 + bob, col); R(bx, 0, lw, 1, SHOE); }
    }
  }
  R(x0, tt, tw, th, S.coat);                                       // the coat, shaded on the far edge and the hem
  if (view === 0) R(x0, tt, 1, th, coatD); else { R(x0, tt + 1, 1, th - 1, coatD); R(x0 + tw - 1, tt + 1, 1, th - 1, coatD); }
  if (S.long && !sit) { const sk = S.long === true ? S.coat : S.long, n = Math.max(2, Math.round(lh * 0.45)); R(x0, tb + 1, tw, n, sk); R(x0, tb + 1, 1, n, darker(sk)); R(x0, tb + n, tw, 1, darker(sk)); }
  else R(x0, tb, tw, 1, coatD);
  if (S.scarf) R(x0 + 1, tt, tw - 2, 1, S.scarf);
  if (S.sash) R(x0, tb - 1, tw, 1, S.sash);
  if (S.pack) { if (view === 0) R(x0 - 1 - (big ? 1 : 0), tt + 1, 1 + (big ? 1 : 0), th - 2, S.pack); else if (view === 1) R(x0 + 1, tt + 1, tw - 2, th - 2, S.pack); }
  if (S.bag) { if (view === 0) R(x0 - 1, tb - 2, 2 + (big ? 1 : 0), 3, S.bag); else R(x0 + tw - 1, tb - 2, 2, 3, S.bag); }
  // arms
  const fx = x0 + tw;                                              // the column just in front of the chest
  if (arm === 0 && view === 0) { const ax = x0 + 1 + (stride === 1 ? 0 : stride === -1 ? tw - 3 : (tw >> 1) - 1); R(ax, tt + 1, 1, th - 2, coatD); put(ax, tb - (sit ? 1 : 0), S.skin); }
  else if (arm === 1) { R(fx - 1, tt + 2, 2 + (big ? 1 : 0), 1, coatD); put(fx + 1 + (big ? 1 : 0), tt + 2, S.skin); }
  else if (arm === 2) {
    if (view === 0) { put(fx - 1, tt, coatD); put(fx, tt - 1, coatD); put(fx, tt - 2, coatD); put(fx + 1, tt - 3, S.skin); }
    else { R(x0 - 1, tt - 2, 1, 4, coatD); R(fx, tt - 2, 1, 4, coatD); put(x0 - 1, tt - 3, S.skin); put(fx, tt - 3, S.skin); }
  } else if (arm === 3) { R(x0 - 1, tt - 3, 1, 4, coatD); R(fx, tt - 3, 1, 4, coatD); put(x0 - 1, tt - 4, S.skin); put(fx, tt - 4, S.skin); }
  else if (arm === 4) { R(x0 - 2, tt + 1, 2, 1, S.coat); R(fx, tt + 1, 2, 1, S.coat); }
  else if (arm === 5) { put(fx, tt + 2, coatD); put(fx + 1, tt + 1, S.skin); if (view !== 0) put(x0 - 1, tt + 2, coatD); }
  // head
  const hb = tt - 1, ht = hb - hh + 1, hx0 = view === 0 ? x0 + tw - hw - (tw - hw >= 2 ? 1 : 0) : x0 + ((tw - hw) >> 1), fh = hh >= 4 ? hh - 2 : 2;
  R(hx0, ht, hw, hh, S.hair);
  if (view === 0) { R(hx0 + hw - 2, hb - fh + 1, 2, fh, S.skin); if (S.beard) R(hx0 + hw - 2, hb - 1, 2, 3, S.beard); }
  else if (view === 2) { R(hx0 + (hw === 4 ? 1 : 0), hb - fh + 1, hw === 4 ? 2 : 3, fh, S.skin); if (S.beard) R(hx0 + (hw === 4 ? 1 : 0), hb, hw === 4 ? 2 : 3, 2, S.beard); }
  if (S.longHair) { if (view === 0) R(hx0, hb + 1, 1 + (big ? 1 : 0), 3, S.hair); else if (view === 1) R(hx0, hb + 1, hw, 3, S.hair); }
  if (S.hat === 'cap') { R(hx0, ht, hw, hh >= 5 ? 2 : 1, S.hatCol); if (view === 0) put(hx0 + hw, ht + (hh >= 5 ? 1 : 0), S.hatCol); }
  else if (S.hat === 'sun') { R(hx0 - 1, ht, hw + 2, 1, S.hatCol); R(hx0, ht - 1, hw, 1, S.hatCol); }
  else if (S.hat === 'kal') R(hx0, ht - 2, hw, 3, '#0e0c14');
}
const SPR_W = 20, SPR_AX = 9;                                      // every figure sprite: 20 wide, the centre column at 9
function figure(S, pose, flip = false) {
  const hgt = S.h + 9, p = painter(SPR_W, hgt);
  drawFigure((i, j, col) => p.put(flip ? SPR_W - 1 - SPR_AX - i : SPR_AX + i, hgt - 1 + j, col), S, pose);
  const c = p.done(); c.flip = flip;
  return c;
}
// x: the figure's centre column, fy: its feet row
const blit = (spr, x, fy) => ctx.drawImage(spr, Math.round(x) - (spr.flip ? SPR_W - 1 - SPR_AX : SPR_AX), fy - spr.height + 1);
const WALK_POSES = [{ bob: 1 }, { stride: 1 }, { bob: 1 }, { stride: -1 }];
function walkSet(S, flip) { const a = figure(S, WALK_POSES[0], flip); return [a, figure(S, WALK_POSES[1], flip), a, figure(S, WALK_POSES[3], flip)]; }
// where a hand held out (arm 1) is, from the centre column and the feet row
function handAt(S) { const { tw, th, lh } = dims(S.h); return { x: -((tw - 1) >> 1) + tw + 1 + (S.h >= 20 ? 1 : 0), y: -lh - th + 3 }; }

const COATS = ['#2a3456', '#6a2a34', '#3a5a3a', '#d8c8a8', '#3a3a44', '#b84a3a', '#2e6a8a', '#8a6a4a', '#e6e2da', '#5a3a6a', '#c8a03a',
  '#1e2230', '#a85a7a', '#4a7a8a', '#7a8a4a', '#d87a4a'];
const HAIRS = ['#141014', '#2a1a14', '#4a2e1a', '#8a5a2a', '#c8a060', '#9a9aa2', '#5a1a14', '#141014'];
const SKINS = ['#e8b890', '#d8a07a', '#c88a64', '#f0c8a0', '#a8704a'];
const LEGC = ['#16121c', '#20243a', '#2a2e3e', '#3a3a4a', '#4a4038', '#c8c0b0', '#1e2a2a'];
const of = (r, list) => list[Math.floor(r() * list.length)];
function randomStyle(r, h) {
  const S = { h, coat: of(r, COATS), legs: of(r, LEGC), skin: of(r, SKINS), hair: of(r, HAIRS) };
  const v = r(), b = r();
  if (v < 0.16) { S.hat = 'cap'; S.hatCol = of(r, COATS); } else if (v < 0.24) { S.hat = 'sun'; S.hatCol = '#e8d8a8'; }
  if (r() < 0.3) S.long = r() < 0.5 ? true : of(r, COATS);
  if (r() < 0.32) S.longHair = true;
  if (b < 0.28) S.bag = of(r, ['#e8d8b0', '#d0343e', '#1e2230', '#f0c040', '#8a5a2a']); else if (b < 0.42) S.pack = of(r, ['#c8483a', '#2e4a7a', '#3a3a44', '#4a7a4a']);
  if (r() < 0.18) S.scarf = of(r, ['#e84a6a', '#f0c040', '#e8e0d0', '#4a9ad0']);
  return S;
}

/* ================= The strollers: a steady stream across the whole square, nearest last ================= */
// Each crosses n times a loop on his own row; the far ones are smaller and slower. Those who set out after the
// Resurrection carry a lit candle home.
const WALKERS = [];
{
  const r = rng(1821);
  const rows = [191, 192, 194, 196, 198, 200, 203, 205, 208, 210, 213, 215, 218, 220, 223, 233, 236, 239, 242, 245, 248, 252, 256, 261];
  // crowd: a couple, a parent and child (`same`) share the unit of the stroller before them and so its rank; a dog
  // and a pram are drawn with their owner, as are the shadow and the candle
  let unit = -1;
  const add = (y, n, ph, dir, S, extra = {}, same = false) => { if (!same) unit++; WALKERS.push({ y, n, ph, dir, S, candle: r() < 0.6, rank: spread(unit), ...extra }); };
  rows.forEach((y, i) => {
    const h = personH(y), n = clamp(Math.round(lerp(3.6, 8.4, (y - 190) / 72) + (r() - 0.5) * 2), 3, 9), dir = (i + (r() < 0.3 ? 1 : 0)) & 1 ? 1 : -1, ph = r();
    add(y, n, ph, dir, randomStyle(r, h + (r() < 0.3 ? -1 : 0)));
    const v = r(), gap = (4 + h * 0.35) / (W + 36);
    if (v < 0.3) add(y, n, ph + gap, dir, randomStyle(r, h - 1 - Math.floor(r() * 2)), {}, true);                // a couple
    else if (v < 0.42) add(y, n, ph + gap, dir, { ...randomStyle(r, Math.round(h * 0.62)), long: false, hat: null, bag: null, pack: null }, { kid: true }, true);   // a child alongside
    else if (v < 0.5) WALKERS[WALKERS.length - 1].dog = true;
    else if (v < 0.56) WALKERS[WALKERS.length - 1].pram = true;
    else if (v < 0.64 && y < 240) add(y + 1, clamp(n + 2, 3, 9), r(), -dir, randomStyle(r, h));               // someone the other way
    if (i % 3 === 1) add(y, clamp(n + (r() < 0.5 ? 1 : -1), 3, 9), ph + 0.37 + r() * 0.2, -dir, randomStyle(r, h));
  });
}
const DOG_ROWS = [['......e.', '.....ddd', 'tdddddd.', '.d...d..'], ['......e.', '.....ddd', 'tdddddd.', '..d.d...']];
const PRAM_ROWS = ['..hhhh...', '.hhhhhh..', '.bbbbbbb.', '.bbbbbbb.', '..bbbbb..', '..k...k..', '.kkk.kkk.', '..k...k..'];
let DOG_SPR, PRAM_SPR;
function buildWalkers() {
  for (const w of WALKERS) w.spr = walkSet(w.S, w.dir < 0);
  DOG_SPR = [['#7a4020', '#3a1e0e'], ['#d8d0c0', '#8a8478']].map(([d, e]) => DOG_ROWS.map(rows => [sprite(rows, { e, d, t: d }), sprite(rows, { e, d, t: d }, true)]));
  PRAM_SPR = [sprite(PRAM_ROWS, { h: '#2e4a7a', b: '#3a3a44', k: '#14121a' }), sprite(PRAM_ROWS, { h: '#2e4a7a', b: '#3a3a44', k: '#14121a' }, true)];
}
const CANDLE_FROM = 116, CANDLE_TO = 214;                          // crossings that start in this window bring the flame
function drawCandle(x, y, seed, t) {                                // a lit taper in a hand at (x, y): its red cup, the flame
  const f = hash(seed, step(t + seed * 0.013, 1920), 7);
  px(ctx, x, y - 1, '#f4ecd8'); px(ctx, x, y - 2, f < 0.3 ? '#fff6c8' : '#ffc850');
  if (f > 0.55) px(ctx, x, y - 3, '#ffa030');
  halo(HL.candle, x, y - 2, 0.75 + 0.25 * f);
}
function drawWalker(w, i, t, fr) {
  const span = W + 36, u = frac(t * w.n / LOOP + w.ph), x = Math.round(w.dir > 0 ? -18 + u * span : W + 18 - u * span);
  if (x < -14 || x > W + 14) return;
  const f = (fr + i * 3) & 3, spr = w.spr[f], fy = w.y;
  ctx.globalAlpha = 0.3; ctx.fillStyle = '#0a0812'; ctx.fillRect(x - 2, fy + 1, 5, 1); ctx.globalAlpha = 1;
  blit(spr, x, fy);
  if (w.dog) { const d = DOG_SPR[i & 1][f & 1][w.dir > 0 ? 0 : 1], dx = x + w.dir * 11; ctx.drawImage(d, dx - 4, fy - 3); line(ctx, x + w.dir * 2, fy - Math.round(w.S.h * 0.5), dx + w.dir * 2, fy - 2, '#8a2a2a'); }
  if (w.pram) ctx.drawImage(PRAM_SPR[w.dir > 0 ? 0 : 1], x + (w.dir > 0 ? 4 : -12), fy - 7);
  if (w.candle && !w.kid) {
    const ts = (((t - u * LOOP / w.n) % LOOP) + LOOP) % LOOP;
    if (ts >= CANDLE_FROM && ts <= CANDLE_TO) { const hd = handAt(w.S); drawCandle(x + w.dir * (hd.x - 2), fy + hd.y + 1 - (f & 1 ? 0 : 1), i, t); }
  }
}

/* ================= The timeline's big moments (loop seconds) ================= */
const DARK = { a: 92, b: 110 };                       // the church puts out its lights before midnight...
const FLAME = 108;                                    // ...the priest brings the flame to the door
const BELLS = [119, 121.2, 123.4, 125.6, 127.8, 130, 132.2, 134.4];   // Christos Anesti: the bell peals
// the line dance: the troupe links arms (in0), dances slowly (s0), the tune picks up (g0), the dance speeds up (d0 .. d1),
// a cheer (.. c1), they drift back (.. back) and catch their breath until the loop comes round
const DANCE = { in0: 8, s0: 18, g0: 170, d0: 180, d1: 222, c1: 226, back: 234, y: 228 };
const beat = (t, ph = 0) => Math.floor(t * 4 / 3 + ph) & 1;          // 80 beats a minute, like the music
const churchLight = t => 1 - env(t, DARK.a, DARK.b, 2.5);
const mk = (h, coat, legs, hair, extra = {}) => ({ h, coat, legs, hair, skin: '#e8b890', ...extra });

/* ================= The duo: a bouzouki and a guitar, an open case, notes in the air ================= */
let DUO_SPR, NOTE_SPR;
function buildDuo() {
  const h = personH(DUO.y);
  DUO_SPR = [figure(mk(h, '#e8e0d0', '#20243a', '#9a9aa2', { hat: 'cap', hatCol: '#1e2230', beard: '#c8c8cc' }), { sit: true, arm: 1 }),
    figure(mk(h + 1, '#3a5a3a', '#16121c', '#2a1a14', { longHair: true }), { arm: 1 }, true)];
  NOTE_SPR = [sprite(['.#', '.#', '##', '##'], { '#': '#ffe9a0' }), sprite(['.##', '.#.', '##.', '##.'], { '#': '#ffd070' })];
}
function drawDuo(t) {
  const { x, y } = DUO, s = step(t, 1920);
  rect(ctx, x - 9, y - 4, 5, 4, '#3a2a22'); rect(ctx, x - 9, y - 5, 5, 1, '#6a4a32');                 // his stool
  blit(DUO_SPR[0], x - 7, y);
  // the bouzouki: a pear-shaped bowl in his lap, the long neck up to the right, the strumming hand
  rect(ctx, x - 5, y - 9, 4, 3, '#b87a3a'); rect(ctx, x - 4, y - 10, 3, 1, '#d89a50'); px(ctx, x - 4, y - 8, '#3a2416');
  line(ctx, x - 1, y - 10, x + 6, y - 14, '#6a4226'); px(ctx, x + 7, y - 15, '#e8d090');
  px(ctx, x - 3 + (s & 1), y - 8 - ((s >> 1) & 1), '#f0c8a0');
  blit(DUO_SPR[1], x + 12, y);
  rect(ctx, x + 6, y - 11, 5, 4, '#c88a48'); rect(ctx, x + 7, y - 12, 3, 1, '#e0a860'); px(ctx, x + 8, y - 10, '#2a1810');   // the guitar
  line(ctx, x + 5, y - 11, x - 1, y - 16, '#5a3a22');
  px(ctx, x + 8 + ((s + 1) & 1), y - 9, '#e8b890');
  rect(ctx, x - 2, y + 2, 12, 3, '#1c1620'); rect(ctx, x - 1, y + 2, 10, 2, '#6a2a34');                 // the open case, a few coins
  if (hash(3, step(t, 480), 12) < 0.5) px(ctx, x + 2 + (step(t, 240) % 5), y + 3, '#ffe070');
  rect(ctx, x + 18, y - 4, 3, 4, '#2a2630'); px(ctx, x + 19, y - 3, '#ffe2a0');                         // a lantern at their feet
  halo(HL.lantern, x + 19, y - 3, 0.85 + 0.15 * wave(t, 420, 0.2));
  const fast = t >= DANCE.g0 && t < DANCE.c1;
  for (let k = 0; k < (fast ? 5 : 3); k++) {            // notes drifting up
    const { cyc, u } = cycle(t, fast ? 96 : 60, k / 5 + 0.07 * k);
    ctx.globalAlpha = Math.sin(Math.PI * u) * 0.9;
    ctx.drawImage(NOTE_SPR[(cyc + k) & 1], Math.round(x + 2 + hash(k, cyc, 13) * 12 - 6 + Math.sin(u * 6 + k) * 2), Math.round(y - 20 - u * 16));
  }
  ctx.globalAlpha = 1;
}

/* ================= Their ring of listeners: [x, feet row, view (0 right, 1 back, 2 front, 3 left), film?] ================= */
const LISTENERS = [[244, 215, 2], [253, 214, 2], [282, 214, 2], [291, 216, 2, 1], [234, 221, 0], [298, 222, 3], [307, 220, 3],
  [248, 231, 1], [284, 232, 1], [294, 230, 1]];
// crowd: each listener's place in the order they stay (0 stays longest): the two at the sides and the one filming
// last, so the ring opens evenly round the duo and never from one side; rank = (place + 0.5) / 10
[8, 4, 7, 2, 1, 0, 6, 5, 3, 9].forEach((place, i) => { LISTENERS[i].rank = (place + 0.5) / 10; });
function buildListeners() {
  const r = rng(77);
  LISTENERS.forEach((L, i) => {
    const S = randomStyle(r, personH(L[1]) + (i % 3 === 0 ? -1 : 0)), v = L[2] === 3 ? 0 : L[2], flip = L[2] === 3;
    L.S = S; L.spr = [figure(S, { view: v }, flip), figure(S, { view: v, arm: L[3] ? 2 : 5 }, flip), figure(S, { view: v, arm: 3 }, flip)];
  });
}
function drawListener(L, i, t) {
  const dancing = t >= DANCE.d0 && t < DANCE.d1, wow = t > 124 && t < 170 && hash(i, Math.floor(t / 1.5), 14) < 0.3;   // arms up at the fireworks
  const cheer = (t >= DANCE.d1 && t < DANCE.c1) || wow;
  const sway = Math.round(wave(t, 30 + i * 4, i * 0.37) * 0.8), b = beat(t, i * 0.5);
  const spr = cheer ? L.spr[2] : L[3] ? L.spr[1] : dancing && b ? L.spr[1] : L.spr[0];
  blit(spr, L[0] + sway, L[1] - (b && i % 2 ? 1 : 0));
  if (L[3]) { const hy = L[1] - L.S.h - 3; px(ctx, L[0] + sway + 3, hy, '#dfeaff'); halo(HL.phone, L[0] + sway + 3, hy, 0.6); }   // filming
}

/* ================= The dancers: a troupe of six who link arms and dance a syrtaki, slow for most of the loop, then faster and faster ================= */
const DANCERS = [0, 1, 2, 3, 4, 5].map(i => ({ loose: 168 + i * 11 + [0, 3, -2, 2, -1, 3][i], lineX: 176 + i * 9 }));
const danceSteps = t => { const s = clamp(t - DANCE.d0, 0, DANCE.d1 - DANCE.d0); return s * 1.3 + s * s * 0.028; };
const SIDE = [0, 2, 4, 6, 4, 2, 0, -2, -4, -6, -4, -2];
function buildDancers() {
  const h = personH(DANCE.y);                           // a troupe: white shirts, red sashes, the women in long red skirts
  DANCERS.forEach((D, i) => {
    const S = mk(h - (i % 2), '#f2eee6', '#16121c', HAIRS[i], { longHair: i % 2 === 1, long: i % 2 === 1 ? '#b8303a' : false, sash: '#c8303a', skin: SKINS[i % 5] });
    D.S = S; D.walk = [walkSet(S, false), walkSet(S, true)]; D.side = figure(S, {});
    D.front = [{ arm: 4 }, { arm: 4, kick: 1 }, { arm: 4, bob: 1 }, { arm: 4, kick: -1 }].map(p => figure(S, { view: 2, ...p }));
    D.cheer = figure(S, { view: 2, arm: 3 });
  });
}
function drawDancer(D, i, t, fr) {
  const y = DANCE.y;
  if (t >= DANCE.in0 && t < DANCE.back) {
    if (t < DANCE.s0 - 2 || t >= DANCE.c1) {            // walking between the loose group and the line
      const u = t < DANCE.s0 ? smooth(DANCE.in0 + i * 0.6, DANCE.s0 - 2, t) : 1 - smooth(DANCE.c1, DANCE.back - (5 - i) * 0.6, t);
      const x = lerp(D.loose, D.lineX, u), moving = u > 0.01 && u < 0.99, dir = (D.lineX > D.loose) === (t < DANCE.s0) ? 0 : 1;
      blit(moving ? D.walk[dir][(fr + i) & 3] : u >= 0.99 ? D.front[0] : D.side, x, y);
    } else if (t < DANCE.s0) blit(D.front[beat(t) ? 2 : 0], D.lineX, y);              // arms on shoulders, waiting for the tune
    else if (t < DANCE.d0) {                            // the slow part: a step every other beat, feet together on the beat between
      const b = Math.floor(t * 4 / 3), q = b >> 1, f = b & 1 ? 0 : q & 3;
      blit(D.front[f], D.lineX + SIDE[q % 12], y - (f === 2 ? 1 : 0));
    } else if (t < DANCE.d1) { const q = Math.floor(danceSteps(t)); blit(D.front[q & 3], D.lineX + SIDE[q % 12], y - ((q & 3) === 2 ? 1 : 0)); }
    else blit(D.cheer, D.lineX, y - (hash(i, step(t, 960), 15) < 0.5 ? 1 : 0));       // the cheer at the end
    return;
  }
  blit(D.side, D.loose + Math.round(wave(t, 24 + i * 5, i * 0.3) * 0.8), y - (beat(t, i * 0.5) && i % 2 ? 1 : 0));
}

/* ================= Guests: people who walk in from an edge, stand a while and walk off again ================= */
// the congregation outside the church door (they hold a candle; it is lit at G.lit) and the onlookers the dance draws
const GUESTS = [];
const SPEED = 15;                                     // px a second for the nearest; smaller figures walk slower
{
  const r = rng(33), spots = [[62, 236], [75, 237], [89, 236], [104, 237], [68, 244], [83, 243], [98, 244], [115, 243], [131, 244], [61, 252], [79, 251], [95, 252], [113, 251]];
  const tIn = [226, 232, 8, 238, 20, 228, 34, 46, 72, 56, 236, 64, 80], tOut = [196, 176, 188, 208, 180, 200, 172, 192, 184, 204, 212, 178, 194];
  // crowd: 0 = the small flock by the door, cast (the flame needs candles to pass to); the others thin in this order
  // of units, each of the two children with the grown-up standing next to it
  const flock = [0, 4, 2, 7, 0, 1, 1, 8, 3, 0, 5, 5, 6];
  spots.forEach(([x, y], i) => {
    const h = personH(y), kind = i % 13;
    const S = kind === 1 || kind === 9 ? mk(h - 2, '#1c1a22', '#1c1a22', '#2a2830', { long: true, skin: '#d8a07a' })          // yiayia in black, a headscarf
      : kind === 5 || kind === 11 ? { ...randomStyle(r, Math.round(h * 0.64)), hat: null, bag: null, pack: null, long: false }   // children
      : kind % 4 === 0 ? mk(h, '#20243a', '#16121c', of(r, HAIRS), { skin: of(r, SKINS) })                                      // Sunday suits
      : { ...randomStyle(r, h), pack: null, hat: null };
    GUESTS.push({ x, y, S, from: i % 3 === 2 ? 1 : -1, to: [-1, 1, -1, -1, 1, -1, 1, 1, 1, -1, -1, 1, 1][i], tIn: tIn[i], tOut: tOut[i], face: -1, candle: true,
      lit: FLAME + 1.5 + Math.hypot(x - 40, (y - 241) * 2) / 7, kid: kind === 5 || kind === 11, rank: flock[i] ? spread(flock[i] - 1) : CAST });
  });
  [[182, 221, 2], [197, 220, 2], [211, 221, 2], [225, 220, 2], [158, 229, 0], [243, 228, 0]].forEach(([x, y, view], i) =>      // the dance's onlookers thin
    GUESTS.push({ x, y, S: randomStyle(r, personH(y)), from: i % 2 ? 1 : -1, to: i % 2 ? -1 : 1, tIn: 158 + i * 1.7 - (i % 2 ? 6 : 0), tOut: 226 + i * 1.3, face: view === 2 ? 0 : i === 5 ? -1 : 1, view, clap: true, rank: spread(i) }));
  [[19.4, 45, -1], [72.3, 113, 1], [141.4, 167, -1], [180.3, 221, 1]].forEach(([a, b, side], k) =>          // someone buys at the fruit cart (cast: the sale is about them)
    GUESTS.push({ x: CART.x + 9 + k * 2, y: CART.y + 4, S: randomStyle(r, personH(CART.y + 4)), from: side, to: -side, tIn: a, tOut: b, face: 1, view: 1, rank: CAST }));
}
function buildGuests() {
  for (const G of GUESTS) {
    const v = G.view || 0, flip = G.face < 0;
    G.walk = [walkSet(G.S, false), walkSet(G.S, true)];
    G.stand = [figure(G.S, { view: v, arm: G.candle ? 1 : 0 }, flip), figure(G.S, { view: v, arm: G.candle ? 1 : 5 }, flip), figure(G.S, { view: v, arm: 3 }, flip)];
    G.hand = handAt(G.S); G.speed = SPEED * (0.55 + 0.45 * (G.y - 190) / 60);
  }
}
function drawGuest(G, i, t, fr) {
  const x0 = G.from < 0 ? -16 : W + 16, x1 = G.to < 0 ? -16 : W + 16;
  const rel = (t - G.tIn + LOOP) % LOOP, dIn = Math.abs(G.x - x0) / G.speed, stay = (G.tOut - G.tIn + LOOP) % LOOP, dOut = Math.abs(x1 - G.x) / G.speed;
  const lit = G.candle && (t - G.lit + LOOP) % LOOP < G.tOut + dOut - G.lit;       // from its lighting until the walk home ends
  let x = G.x, dir = G.face;
  if (rel < dIn) { x = lerp(x0, G.x, rel / dIn); dir = G.x > x0 ? 1 : -1; blit(G.walk[dir > 0 ? 0 : 1][(fr + i) & 3], x, G.y); }
  else if (rel < stay) {
    const cheer = G.clap && t >= DANCE.d1 && t < DANCE.c1, clap = G.clap && t >= DANCE.d0 && t < DANCE.d1 && beat(t, i * 0.5);
    const hop = G.kid && hash(i, step(t, 480), 16) < 0.3 ? 1 : 0, shift = hash(i, step(t, 60), 17) < 0.25 ? 1 : 0;
    x += shift; blit(G.stand[cheer ? 2 : clap ? 1 : 0], x, G.y - hop);
    if (G.candle) { const hx = Math.round(x) + dir * G.hand.x, hy = G.y + G.hand.y - hop; if (lit) drawCandle(hx, hy, 40 + i, t); else px(ctx, hx, hy - 1, G.kid ? '#f0a0c0' : '#e8e0d0'); }
    return;
  } else if (rel < stay + dOut) { x = lerp(G.x, x1, (rel - stay) / dOut); dir = x1 > G.x ? 1 : -1; blit(G.walk[dir > 0 ? 0 : 1][(fr + i) & 3], x, G.y); }
  else return;
  if (lit) drawCandle(Math.round(x) + dir * (G.hand.x - 2), G.y + G.hand.y + 1, 40 + i, t);
}

/* ================= The souvlaki window: the spit turning, the cook, a queue that moves up every 20 s ================= */
// n turns a loop; slot 0 is at the window. crowd: a rank per slot, so the queue shortens from its tail and the one
// being served is always there
const QUEUE = { y: 192, slots: [412, 401, 391, 381], n: 12, rank: [0, 0.25, 0.5, 0.75] };
let QUEUE_SPR;
function buildQueue() {
  const r = rng(404), h = personH(QUEUE.y);
  QUEUE_SPR = [0, 1, 2, 3, 4, 5].map(() => { const S = { ...randomStyle(r, h), hat: null }; return { back: figure(S, { view: 1 }), side: figure(S, {}), walk: walkSet(S, false), S }; });
}
function drawCook(t, crowd) {                           // crowd 0: the grill is on and the spit turns, nobody carves
  const x = SOUV.x, s = step(t, 960), gy = 172;
  rect(ctx, x + 3, gy + 1, 2, 9, '#ff8a3a'); halo(HL.grill, x + 5, gy + 6, 0.8 + 0.2 * wave(t, 360, 0.1));        // the grill's elements
  [3, 4, 4, 5, 5, 4, 4, 3].forEach((w, k) => {          // the gyros cone, turning
    for (let i = 0; i < w; i++) px(ctx, x + 8 - (w >> 1) + i, gy + 1 + k, ['#8a4a26', '#b06a34', '#6a341c', '#c8824a'][(i + k + s) & 3]);
  });
  rect(ctx, x + 8, gy, 1, 11, '#c8c8d0');
  if (crowd !== 0) {
    const cx = x + 15 + Math.round(wave(t, 16, 0.1) * 3 + wave(t, 44, 0.4) * 1.5);                              // the cook, carving and wrapping
    rect(ctx, cx, gy + 1, 3, 1, '#f4f0e8'); rect(ctx, cx, gy + 2, 3, 2, '#e8b890'); rect(ctx, cx - 1, gy + 4, 5, 6, '#f4f0e8'); rect(ctx, cx, gy + 5, 3, 5, '#d8303a');
    px(ctx, cx - 2 + (s & 1), gy + 5 + ((s >> 1) & 1), '#e8b890');
  }
  for (let k = 0; k < 4; k++) rect(ctx, x + 14 + k * 3, gy + 9, 2, 1, k & 1 ? '#f0e0b0' : '#c85a3a');              // pittas and tomatoes on the counter
}
function drawQueue(t, fr, crowd) {
  const { y, slots } = QUEUE, { cyc, u } = cycle(t, QUEUE.n), who = k => QUEUE_SPR[(cyc + k + 6) % 6];
  let len = 1;                                          // how many stand in line at this crowd level (4 by default)
  while (len < slots.length && QUEUE.rank[len] < crowd) len++;
  const prev = slots[0] + 36 + u * 180;                 // last turn's customer, walking off with his souvlaki
  if (prev < W + 12) { blit(who(-1).walk[(fr + 1) & 3], prev, y + 2); px(ctx, Math.round(prev) + 3, y - 4, '#f4f0e0'); }
  if (u < 0.8) {
    blit(who(0).back, slots[0], y);
    if (u > 0.55) { px(ctx, slots[0] + 1, y - 9, '#f4f0e0'); px(ctx, slots[0] + 1, y - 8, '#f4f0e0'); }                  // handed over the counter
    for (let k = 1; k < len; k++) blit(who(k).side, slots[k] + (hash(k, step(t, 80), 22) < 0.2 ? 1 : 0), y);
  } else {
    const v = (u - 0.8) / 0.2;
    blit(who(0).walk[fr & 3], slots[0] + v * 36, y + Math.round(v * 2));
    px(ctx, Math.round(slots[0] + v * 36) + 3, y - 4, '#f4f0e0');
    for (let k = 1; k < len; k++) blit(who(k).walk[(fr + k) & 3], lerp(slots[k], slots[k - 1], v), y);
    blit(who(len).walk[(fr + 2) & 3], lerp(slots[len - 1] - 30, slots[len - 1], v), y);    // someone new joins the tail
  }
}

/* ================= The fruit cart's seller, the koulouri seller ================= */
let VENDOR_SPR, STAND_C;
function buildVendors() {
  const fruit = mk(personH(CART.y - 2), '#3a5a3a', '#20243a', '#2a1a14', { hat: 'cap', hatCol: '#e8e0d0' });
  const koul = mk(personH(198), '#e6e2da', '#20243a', '#9a9aa2', { hat: 'cap', hatCol: '#2a3456' });
  VENDOR_SPR = { fruit: [figure(fruit, { view: 2 }), figure(fruit, { view: 2, arm: 5 }), figure(fruit, { view: 2, arm: 3 })], koul: [figure(koul, {}, true), figure(koul, { arm: 1 }, true)] };
  STAND_C = renderStand();
}
const SALES = [[38, 44], [106, 112], [160, 166], [214, 220]];        // someone is at the cart: the seller fills a cone and hands it over
function drawFruitSeller(t) {
  const sale = inEvent(t, SALES), s = step(t, 480);
  blit(VENDOR_SPR.fruit[sale ? 1 + (s & 1) : hash(5, step(t, 60), 23) < 0.3 ? 1 : 0], CART.x + 22 + (sale ? -4 : 0), CART.y - 4);
}
function drawCart(t) {
  ctx.drawImage(CART_C, CART.x, CART.y - 29);
  const fl = 0.9 + 0.1 * wave(t, 620, 0.3);
  rect(ctx, CART.x + 16, CART.y - 25, 3, 3, '#fffbe4'); halo(HL.bulb, CART.x + 17, CART.y - 24, fl); halo(HL.bulbCore, CART.x + 17, CART.y - 24, fl);
  const { u } = cycle(t, 80, 0.2);                       // water trickling over the coconut slices
  px(ctx, CART.x + 9, CART.y - 11 + Math.floor(u * 3), '#dff4ff'); px(ctx, CART.x + 25, CART.y - 11 + Math.floor(frac(u + 0.5) * 3), '#dff4ff');
}
function drawKoulouri(t, fr, crowd) {                   // crowd 0: the stand of sesame rings on its own
  ctx.drawImage(STAND_C, 236, 198 - 15);
  if (crowd !== 0) blit(VENDOR_SPR.koul[hash(6, step(t, 48), 24) < 0.35 ? 1 : 0], 255, 198);
}

/* ================= Two kids and a ball; now and then one of them charges the pigeons ================= */
const KIDS = { a: 322, b: 372, y: 245, chase: [[22, 31], [146, 155]] };
let KID_SPR;
function buildKids() {
  const h = Math.round(personH(KIDS.y) * 0.62);
  KID_SPR = [mk(h, '#d8303a', '#20243a', '#2a1a14'), mk(h, '#f0c040', '#3a3a4a', '#8a5a2a', { longHair: true })].map((S, k) =>
    ({ stand: figure(S, {}, k === 1), kick: figure(S, { stride: 1 }, k === 1), run: [walkSet(S, false), walkSet(S, true)] }));
}
const chaseOf = t => { for (const [a, b] of KIDS.chase) if (t >= a && t < b) return { a, b, v: t < a + 3.5 ? (t - a) / 3.5 : 1 - (t - a - 3.5) / (b - a - 3.5) }; return null; };
function drawKids(t, fr) {
  const { a, b, y } = KIDS, ch = chaseOf(t), { cyc, u } = cycle(t, 96);
  if (ch) {                                             // he runs at the birds and trots back; the ball waits with her
    const out = t < ch.a + 3.5;
    blit(KID_SPR[0].run[out ? 1 : 0][(fr * 2 + 1) & 3], a - ch.v * 44, y);
    blit(KID_SPR[1].stand, b, y + 1);
    rect(ctx, b - 6, y - 1, 3, 3, '#f4f0e8'); px(ctx, b - 5, y, '#d8303a');
    return;
  }
  const toB = (cyc & 1) === 0;
  blit(toB && u < 0.2 ? KID_SPR[0].kick : KID_SPR[0].stand, a, y);
  blit(!toB && u < 0.2 ? KID_SPR[1].kick : KID_SPR[1].stand, b, y + 1);
  const bx = Math.round(lerp(a + 5, b - 6, toB ? u : 1 - u)), by = Math.round(y - 1 - Math.abs(Math.sin(Math.PI * u * 2)) * (u < 0.5 ? 9 : 4));
  ctx.globalAlpha = 0.3; rect(ctx, bx, y + 2, 3, 1, '#0a0812'); ctx.globalAlpha = 1;
  rect(ctx, bx, by, 3, 3, '#f4f0e8'); px(ctx, bx + ((fr >> 1) & 1) + 1, by + 1, '#d8303a');
}

/* ================= Tourists photographing the rock: phones up, a flash ================= */
const SHOOTERS = [{ x: 336, y: 216, f: [34, 98, 128.5, 160.2] }, { x: 346, y: 217, f: [36.5, 131, 141.3, 232] }, { x: 118, y: 213, f: [12, 66.5, 134.6, 148.4, 190] },
  { x: 204, y: 240, f: [127.2, 137.8, 152.5, 163] }, { x: 420, y: 232, f: [58, 129.6, 144.2, 156.8] }];
SHOOTERS.forEach((S, i) => { S.rank = spread(i); });                 // crowd: they thin, each with the phone and its flash
function buildShooters() {
  const r = rng(505);
  for (const S of SHOOTERS) { const st = randomStyle(r, personH(S.y)); S.S = st; S.spr = [figure(st, { view: 1 }), figure(st, { view: 1, arm: 2 })]; }
}
function drawShooter(S, i, t) {
  let near = 99;
  for (const f of S.f) { const d = t - f; if (d > -5 && d < 2 && Math.abs(d) < Math.abs(near)) near = d; }
  const up = near < 2 && near > -5;
  blit(S.spr[up ? 1 : 0], S.x + (hash(i, step(t, 48), 25) < 0.2 ? 1 : 0), S.y);
  if (!up) return;
  const hy = S.y - S.S.h - 2;
  rect(ctx, S.x - 1, hy, 3, 2, '#1c1c28'); px(ctx, S.x, hy + 1, '#9ac0ff');                                       // the phone's screen
  if (near >= 0 && near < 0.22) { rect(ctx, S.x - 1, hy - 1, 3, 3, '#ffffff'); halo(HL.flash, S.x, hy, 1); }
}

/* ================= The balloon seller: a slow walk across the square under a bobbing bunch; one gets away ================= */
const BSELL = { y: 219, n: 2, ph: 0.3 };
const BAL_COLS = ['#ff4a5a', '#ffd23a', '#4ac8ff', '#ff8ad0', '#7ae06a', '#ffa030', '#b08aff', '#f4f4f4', '#ff4a5a'];
const BAL_OFF = [[-7, -2], [-3, -6], [2, -7], [6, -3], [-5, 3], [0, 0], [5, 2], [-1, -11], [3, 6]];
const ESCAPE = { a: 50, b: 84, rise: 24 };              // the ninth slips its string at a, is far above the roofs by a + rise
let BAL_SPR, BSELL_SPR;
function buildBalloons() {
  BAL_SPR = BAL_COLS.map(c => sprite(['.ccc.', 'chccc', 'chccc', 'ccccd', '.cdd.', '..k..'], { c, h: tint(c, '#ffffff', 0.6), d: darker(c, 0.3), k: darker(c, 0.5) }));
  BSELL_SPR = walkSet(mk(personH(BSELL.y), '#c8a03a', '#3a3a4a', '#141014', { hat: 'sun', hatCol: '#e8d8a8' }), false);
}
const sellerX = t => -18 + frac(t * BSELL.n / LOOP + BSELL.ph) * (W + 36);
function drawBalloonSeller(t, fr) {
  const x = Math.round(sellerX(t)), y = BSELL.y, hx = x + 4, hy = y - 11, bx = x + 5, by = y - 36;
  blit(BSELL_SPR[fr & 3], x, y);
  BAL_OFF.forEach(([dx, dy], k) => {
    if (k === 8 && t >= ESCAPE.a && t < ESCAPE.b) return;
    const px0 = bx + dx + Math.round(wave(t, 40 + k * 7, k * 0.21) * 1.2), py0 = by + dy + Math.round(wave(t, 56 + k * 5, k * 0.37) * 1.2);
    if (k % 2 === 0) { ctx.globalAlpha = 0.5; line(ctx, hx, hy, px0 + 2, py0 + 6, '#b8b4c4'); ctx.globalAlpha = 1; }
    ctx.drawImage(BAL_SPR[k], px0, py0);
  });
}
function drawEscaped(t) {                              // drawn over the roofs: it climbs, swaying, into the night
  if (t < ESCAPE.a || t >= ESCAPE.a + ESCAPE.rise) return;
  const s = (t - ESCAPE.a) / ESCAPE.rise, x0 = sellerX(ESCAPE.a) + 8;
  const x = x0 + 26 * s + Math.sin(s * 14) * 5, y = lerp(BSELL.y - 30, -12, Math.pow(s, 0.85));
  ctx.drawImage(BAL_SPR[8], Math.round(x), Math.round(y));
  for (let k = 0; k < 5; k++) px(ctx, Math.round(x + 2 + Math.sin(s * 14 + k * 0.6) * 1.2), Math.round(y + 6 + k), '#8a8490');
}

/* ================= The taverna's terrace: tables under the orange tree, a waiter with a tray ================= */
// crowd: the couple at a table share its rank. The two tables the waiter serves have their guests while he is there
// (cast); a thinned couple leaves a laid table behind (the cloth, the candle, the chairs); at crowd 0 all four are empty
const TABLES = [{ x: 404, y: 208, rank: CAST }, { x: 470, y: 206, rank: CAST }, { x: 460, y: 217, rank: 0.75 }, { x: 420, y: 219, rank: 0.25 }];
let DINER_SPR, TABLE_C, CHAIR_C, WAITER_SPR;
function buildTerrace() {
  const r = rng(606);
  DINER_SPR = TABLES.flatMap(T => [0, 1].map(k => { const S = { ...randomStyle(r, personH(T.y)), hat: null, pack: null, bag: null }; return [0, 1, 5, 2].map(arm => figure(S, { sit: true, arm }, k === 1)); }));
  TABLE_C = sprite(TABLE_ROWS, { w: '#f4f0e8', c: '#c8c0b4', k: '#2e2a38' });
  CHAIR_C = [sprite(CHAIR_ROWS, { b: '#3a2a22' }), sprite(CHAIR_ROWS, { b: '#3a2a22' }, true)];
  const S = mk(personH(200), '#f4f0e8', '#16121c', '#141014');
  WAITER_SPR = [walkSet(S, false), walkSet(S, true), figure(S, { arm: 1 })];
}
function drawTable(T, k, t, crowd) {
  const s = step(t + k * 0.4, 320), taken = seen(T.rank, crowd);   // they talk with their hands
  for (const side of [0, 1]) {
    const x = T.x + (side ? 9 : -9), pose = hash(k * 2 + side, s, 31), sp = DINER_SPR[k * 2 + side];
    ctx.drawImage(CHAIR_C[side], x + (side ? 1 : -4), T.y - 5);
    if (taken) blit(sp[pose < 0.5 ? 0 : pose < 0.72 ? 1 : pose < 0.9 ? 2 : 3], x, T.y);
  }
  ctx.drawImage(TABLE_C, T.x - 5, T.y - 6);
  if (taken) { px(ctx, T.x - 2, T.y - 7, '#e8e0f0'); px(ctx, T.x + 2, T.y - 7, '#c83a30'); }                        // their glasses
  px(ctx, T.x, T.y - 7, '#f4ecd8');                                                                                 // a candle
  px(ctx, T.x, T.y - 8, hash(k, step(t, 1920), 32) < 0.3 ? '#fff6c8' : '#ffc850');
  halo(HL.candle, T.x, T.y - 8, 0.8);
}
function drawWaiter(t, fr) {                            // out of the door with a tray, to a table, and back: every 30 s
  const { cyc, u } = cycle(t, 8, 0.12), s = u * 30, T = TABLES[cyc % 2 ? 1 : 0], y = 200, dx = 435, tx = T.x + (cyc % 2 ? -6 : 8);
  if (s > 13) return;
  const v = s < 5 ? s / 5 : s < 8 ? 1 : 1 - (s - 8) / 5, x = lerp(dx, tx, v), yy = Math.round(lerp(196, y, Math.min(1, v * 2)));
  const dir = (tx > dx) === (s < 5) ? 0 : 1;
  blit(s >= 5 && s < 8 ? WAITER_SPR[2] : WAITER_SPR[dir][fr & 3], x, yy);
  if (s < 8) rect(ctx, Math.round(x) + (dir ? -5 : 2), yy - 11, 4, 1, '#e8e8f0');                                    // the tray
}

/* ================= People sitting: teenagers on the porch stair and the station's steps ================= */
const SITTERS = [[158, 176, 0], [163, 180, 0], [169, 184, 0], [223, 190, 0], [232, 190, 3], [326, 190, 0], [338, 190, 3], [318, 190, 3]];
SITTERS.forEach((L, i) => { L.rank = spread(i); });                 // crowd: they thin, each with the phone's glow
function buildSitters() {
  const r = rng(707);
  for (const L of SITTERS) { const S = { ...randomStyle(r, 12), hat: null, long: false }; L.spr = [figure(S, { sit: true }, L[2] === 3), figure(S, { sit: true, arm: 1 }, L[2] === 3)]; }
}
function drawSitter(L, i, t) {
  const ph = hash(i, step(t, 40), 33) < 0.45;           // on the phone, or not
  blit(L.spr[ph ? 1 : 0], L[0], L[1]);
  if (ph) { const hx = L[0] + (L[2] === 3 ? -4 : 4); px(ctx, hx, L[1] - 7, '#cfe0ff'); halo(HL.phone, hx, L[1] - 7, 0.5); }
}

/* ================= Out of the metro every minute, and in again ================= */
const COMMUTERS = [];
{
  const r = rng(909);
  // crowd: the first two out of the train and the first one in are cast (the wave happens at every level above 0), the rest thin
  for (let j = 0; j < 9; j++) COMMUTERS.push({ door: STN.doors[1 + j % 3], dir: j % 2 ? 1 : -1, y: 191 + (j * 3) % 8, off: 20 + j * 0.9, S: randomStyle(r, 12 + (j & 1)), out: true, rank: j < 2 ? CAST : spread(j - 2) });
  for (let j = 0; j < 6; j++) COMMUTERS.push({ door: STN.doors[1 + j % 3], dir: j % 2 ? -1 : 1, y: 192 + (j * 3) % 7, off: 44 + j * 2.3, S: randomStyle(r, 12 + (j & 1)), out: false, rank: j < 1 ? CAST : spread(j - 1) });
}
function drawCommuter(C, i, t, fr) {
  const rel = ((t - C.off) % 60 + 60) % 60, sp = 9.5;
  if (C.out) {                                          // down the steps, then away along the front
    const x = C.door + C.dir * Math.max(0, rel - 0.8) * sp;
    if (x < -10 || x > W + 10) return;
    blit(C.spr[(fr + i) & 3], x, Math.round(lerp(188, C.y, Math.min(1, rel / 0.8))));
  } else {                                              // the other way: from the edge to the door
    const edge = C.dir > 0 ? -12 : W + 12, d = Math.abs(C.door - edge) / sp;
    if (rel > d + 0.8) return;
    blit(C.spr[(fr + i) & 3], rel < d ? lerp(edge, C.door, rel / d) : C.door, Math.round(lerp(C.y, 188, clamp((rel - d) / 0.8, 0, 1))));
  }
}

/* ================= Small figures further off: the porch, the roof terraces, lit windows, Areos street ================= */
const ROOF_FOLK = [];
{
  const r = rng(111);
  for (let k = 0; k < 9; k++) ROOF_FOLK.push({ x: BLOCK.x0 + 5 + k * 10 + Math.floor(r() * 4), y: BLOCK.top - 9, c: of(r, COATS), hr: of(r, HAIRS), ph: r() });
  for (let k = 0; k < 6; k++) ROOF_FOLK.push({ x: 6 + k * 10 + Math.floor(r() * 4), y: LEFTB.top - 9, c: of(r, COATS), hr: of(r, HAIRS), ph: r() });
  for (let k = 0; k < 3; k++) ROOF_FOLK.push({ x: PORCH_ARCH[k] + 2, y: MOSQ.pod - 2, c: of(r, COATS), hr: of(r, HAIRS), ph: r(), full: true });
  ROOF_FOLK.forEach((F, i) => { F.rank = spread(i); });             // crowd: all of these thin, and so do the far figures below
}
const STREET_FOLK = [0, 1, 2, 3, 4, 5, 6].map(k => ({ n: 5 + (k * 3) % 4, ph: k * 0.143 + 0.05, c: COATS[(k * 5) % COATS.length], down: k % 2 === 0, rank: spread(k) }));
function drawSmallFolk(t, crowd) {
  const s = step(t, 160);
  ROOF_FOLK.forEach((F, i) => {                         // heads and shoulders at the rail: they lean, turn, lift a glass
    if (F.rank >= crowd) return;
    const lean = hash(i, s + Math.floor(F.ph * 9), 35) < 0.3 ? 1 : 0, x = F.x + lean, up = hash(i, step(t + F.ph * 3, 96), 36) < 0.25;
    rect(ctx, x, F.y - 9, 2, 2, F.hr); px(ctx, x + (i & 1), F.y - 8, '#e8b890');
    rect(ctx, x - 1, F.y - 7, 4, F.full ? 7 : 6, F.c); px(ctx, x - 1, F.y - 7, darker(F.c));
    if (up) { px(ctx, x + (i & 1 ? -2 : 3), F.y - 8, '#e8b890'); px(ctx, x + (i & 1 ? -2 : 3), F.y - 9, '#ffe9a0'); }
  });
  for (const w of FIGWIN) {                             // someone crosses a lit room
    if (w.rank >= crowd) continue;
    const u = frac(t * w.k / LOOP + w.ph);
    if (u > 0.4) continue;
    const x = w.x + Math.round((u / 0.4) * (w.w - 1));
    rect(ctx, x, w.y + 1, 1, w.h - 1, '#3a2418'); px(ctx, x, w.y, '#5a3a22');
  }
  STREET_FOLK.forEach((F, k) => {                       // Areos street: small figures coming down from Plaka and going up
    if (F.rank >= crowd) return;
    const u = frac(t * F.n / LOOP + F.ph), v = F.down ? u : 1 - u, x = Math.round(lerp(210, 182 + k * 4, v)), y = Math.round(lerp(178, GROUND, v)), h = Math.round(lerp(5, 9, v));
    rect(ctx, x, y - h, 2, 2, '#2a1a14'); rect(ctx, x - (h > 6 ? 1 : 0), y - h + 2, h > 6 ? 3 : 2, h - 4, F.c); rect(ctx, x, y - 2, 1 + ((step(t, 960) + k) & 1), 2, '#16121c');
  });
}

/* ================= Scooters: two parked by the lane, a delivery rider threading the far edge of the square ================= */
const SCOOT_ROWS = ['....hhh........', '...hhhh........', '...fhh.........', '..jjjjj.BBBB...', '.ajjjjj.BBBB...', 'ca.jjjj.BBBB...',
  'c..lljjsssss...', 'cm.llsssssssr..', '.mmsssssssss...', '.ww.mmmmm.ww...', 'wwww.....wwww..', '.ww.......ww...'];
const PARKED_ROWS = ['cc.............', '.c.............', '.c....ssss.....', '.cm..sssssss...', '.cmmmsssssssr..', '.ww.mmmmmm.ww..', 'wwww......wwww.', '.ww........ww..'];
const RIDE = { t0: 66, every: 120, dur: 15, y: 195, x0: 372, x1: -22 };
let SCOOT_C, PARKED_C;
function buildScooters() {
  const key = c => ({ h: '#e8e4dc', f: '#e8b890', j: '#2a3456', a: '#1c1a22', B: '#c8303a', c: tint(c, '#ffffff', 0.4), m: c, s: darker(c, 0.15), l: '#20243a', r: '#ff4a3a', w: '#14121a' });
  SCOOT_C = sprite(SCOOT_ROWS, key('#8ac8b0'));
  PARKED_C = [sprite(PARKED_ROWS, key('#d8b048')), sprite(PARKED_ROWS, key('#c85a4a'), true)];
}
function drawParked() { ctx.drawImage(PARKED_C[0], 364, 197); ctx.drawImage(PARKED_C[1], 376, 198); }
function drawRider(t) {
  const rel = ((t - RIDE.t0) % RIDE.every + RIDE.every) % RIDE.every;
  if (rel >= RIDE.dur) return;
  const v = rel / RIDE.dur, x = Math.round(lerp(RIDE.x0, RIDE.x1, v)), y = RIDE.y - Math.round((1 - Math.min(1, rel / 1.2)) * 6);
  ctx.drawImage(SCOOT_C, x, y - 11);
  px(ctx, x - 1, y - 5, '#fffbe0'); halo(HL.lampCore, x - 2, y - 5, 0.9);
  ctx.globalAlpha = 0.25; rect(ctx, x - 14, y, 13, 1, '#ffe9a0'); ctx.globalAlpha = 1;                              // its beam on the stones
}

/* ================= The flags by the church door: Greece's blue and white, the Church's yellow with the eagle ================= */
const FLAG_G = ['bwbwwwwww', 'wwwbbbbbb', 'bwbwwwwww', 'bbbbbbbbb', 'wwwwwwwww', 'bbbbbbbbb'];
const FLAG_B = ['yyyyyyyyy', 'yyykykyyy', 'yyykkkyyy', 'yykkkkkyy', 'yyykykyyy', 'yyyyyyyyy'];
function drawFlag(rows, key, x, y, t, ph, flip) {
  for (let i = 0; i < 9; i++) {
    const off = Math.round(Math.sin(TAU * (100 * t / LOOP + ph) - i * 0.8) * (i / 9) * 1.4);
    for (let j = 0; j < 6; j++) { ctx.fillStyle = key[rows[j][i]]; ctx.fillRect(flip ? x - i : x + i, y + j + off, 1, 1); }
  }
}
function drawFlags(t) {
  rect(ctx, 6, 186, 1, 17, '#b0a898'); rect(ctx, 46, 186, 1, 17, '#b0a898'); px(ctx, 6, 185, '#e8d090'); px(ctx, 46, 185, '#e8d090');
  drawFlag(FLAG_G, { b: '#2a5ab8', w: '#f0f0f4' }, 7, 187, t, 0, false);
  drawFlag(FLAG_B, { y: '#e8c030', k: '#1c1a22' }, 45, 187, t, 0.3, true);
}

/* ================= The church at midnight: its lights, the priest with the flame, the bell ================= */
const PRIEST = { out: 104, at: FLAME + 0.5, back: 150, gone: 154.5, door: CH.door.x + 7, x: 41, y: 241 };
let PRIEST_SPR, DOOR_C, BELL_C;
function buildChurchFx() {
  const S = mk(personH(PRIEST.y) + 1, '#16141c', '#16141c', '#16141c', { long: true, hat: 'kal', beard: '#c8c8cc', scarf: '#e8c860' });
  PRIEST_SPR = { walk: [walkSet(S, false), walkSet(S, true)], up: figure(S, { arm: 2 }), hold: figure(S, { arm: 1 }), S };
  const { w: dw, y: dy } = CH.door, hgt = CH.foot - 1 - (dy - 7);
  DOOR_C = [0, 1, 2, 3].map(n => {                      // crowd: DOOR_C[n] has the n of the three inside who stay longest
    const p = painter(dw, hgt);
    for (let y = 0; y < hgt; y++) for (let i = 0; i < dw; i++) {        // the nave seen through the door: gold light, an icon stand, hanging lamps
      const ay = 7 - y;
      if (ay > 0 && Math.hypot(i + 0.5 - dw / 2, ay) >= dw / 2) continue;
      let col = ['#fff0bc', '#ffe09c', '#f8c878', '#eaac5c', '#d48e44'][clamp(Math.floor((y / hgt + Math.abs(i + 0.5 - dw / 2) / dw * 0.7) * 5), 0, 4)];
      if (i === 0 || i === dw - 1) col = '#8a5a30';                                           // the door's leaves, folded back
      if ((i === 6 || i === 7) && y > 3 && y < 7) col = y === 6 ? '#fffbe0' : '#b8862e';        // a hanging lamp
      for (const [hx, hy, place] of [[3, hgt - 12, 1], [8, hgt - 14, 0], [11, hgt - 11, 2]]) {  // people standing inside, backs to us
        if (place >= n) continue;
        if (Math.abs(i - hx) <= 1 && y >= hy && y < hy + 3) col = '#3a2418';
        if (Math.abs(i - hx) <= (y > hy + 4 ? 2 : 1) && y >= hy + 3) col = '#4a2e22';
      }
      p.put(i, y, col);
    }
    return p.done();
  });
  BELL_C = sprite(['..k..', '.ggg.', '.gGg.', 'ggGgg', 'ggggg', '..d..'], { k: '#3a3038', g: '#d8a848', G: '#fff0a0', d: '#6a4a22' });
}
const NAVE_RANK = [1 / 6, 0.5, 5 / 6];                 // crowd: the three standing inside thin as well
function drawChurch(t, crowd) {
  ctx.drawImage(CHURCH_C, 0, CHC.y);
  drawFlags(t);
  const L = churchLight(t), { x: dx, w: dw, y: dy } = CH.door, dc = dx + dw / 2;
  if (L > 0.02) {
    let inside = 0;
    while (inside < 3 && NAVE_RANK[inside] < crowd) inside++;
    ctx.globalAlpha = L;
    ctx.drawImage(DOOR_C[inside], dx, dy - 7);
    for (const wx of [dc - 5, dc + 1]) { rect(ctx, wx + 1, 168, 2, 1, '#ffe2a0'); rect(ctx, wx, 169, 4, 8, '#ffcf80'); }
    ctx.globalAlpha = 1;
    halo(HL.door, dc, dy + 12, L * (0.85 + 0.1 * wave(t, 300, 0.1)));
  }
  const fl = hash(9, step(t, 1440), 51);                 // the oil lamp before the icon never goes out
  px(ctx, dc, dy - 13, fl < 0.3 ? '#ffb060' : '#ff5a3a'); halo(HL.icon, dc, dy - 13, 0.7 + 0.3 * fl);
  // the bell swings on every stroke
  const d = t - BELLS[0], ring = d > -1 && d < BELLS.length * 2.2 + 2;
  const sw = ring ? Math.round(Math.sin(d / 2.2 * TAU) * 2.4 * Math.min(1, (BELLS.length * 2.2 + 2 - d) / 3)) : 0;
  ctx.drawImage(BELL_C, BELL.x - 2 + sw, BELL.y - 5);
  if (ring) { px(ctx, BELL.x - sw, BELL.y + 1, '#3a3038'); halo(HL.icon, BELL.x, BELL.y - 2, 0.5 + 0.5 * Math.abs(Math.sin(d / 2.2 * Math.PI))); }
}
function drawPriest(t, fr) {
  const Pr = PRIEST;
  if (t < Pr.out || t >= Pr.gone) return;
  let x = Pr.x, spr = PRIEST_SPR.hold, high = false;
  if (t < Pr.at) { x = lerp(Pr.door, Pr.x, (t - Pr.out) / (Pr.at - Pr.out)); spr = PRIEST_SPR.walk[0][fr & 3]; }
  else if (t >= Pr.back) { x = lerp(Pr.x, Pr.door, (t - Pr.back) / (Pr.gone - Pr.back)); spr = PRIEST_SPR.walk[1][fr & 3]; }
  else if (t < FLAME + 7 || hash(11, step(t, 48), 52) < 0.3) { spr = PRIEST_SPR.up; high = true; }
  blit(spr, x, Pr.y);
  const hd = handAt(PRIEST_SPR.S), hx = Math.round(x) + (high ? hd.x - 1 : t >= Pr.back ? -hd.x + 2 : hd.x), hy = Pr.y + hd.y - (high ? 5 : 0);
  rect(ctx, hx, hy - 3, 1, 3, '#f4ecd8'); px(ctx, hx, hy - 4, '#fff6c8'); px(ctx, hx, hy - 5, hash(12, step(t, 1920), 53) < 0.5 ? '#ffc850' : '#ffa030');
  halo(HL.flame, hx, hy - 4, 0.85 + 0.15 * hash(13, step(t, 1920), 54));
}

/* ================= Pigeons: pecking on the paving until something sends them wheeling round the dome ================= */
// [from, to, sent up by the kid who charges them?]: with nobody on the square (crowd 0) only the bell startles them
const FLY = [[25, 39, 1], [119, 135, 0], [149, 163, 1]];
const PIGEONS = [];
{ const r = rng(212); for (let i = 0; i < 15; i++) PIGEONS.push({ x: 256 + Math.floor(r() * 52), y: 238 + Math.floor(r() * 14), d: r() * 1.4, ph: r(), flip: r() < 0.5, c: i % 4 }); }
PIGEONS.sort((a, b) => a.y - b.y);
let PIG_SPR;
function buildPigeons() {
  const keys = [{ b: '#8a8c9c', h: '#5a6a7a', w: '#c8cad8', l: '#c85a4a' }, { b: '#a8a8b4', h: '#4a5a6a', w: '#e0e0ea', l: '#c85a4a' }, { b: '#6a6c7c', h: '#3a4a5a', w: '#a8aab8', l: '#c85a4a' }, { b: '#d8d4cc', h: '#8a8478', w: '#f4f0ea', l: '#c85a4a' }];
  const rows = { stand: ['...hh', 'bbbh.', 'wbbb.', '.l.l.'], peck: ['.....', 'bbb..', 'wbbhh', '.l.l.'], up: ['w...w', '.wbw.', '..bh.'], down: ['.....', 'wbbbh', 'w.b.w'] };
  PIG_SPR = keys.map(k => Object.fromEntries(Object.entries(rows).map(([n, rws]) => [n, [sprite(rws, k), sprite(rws, k, true)]])));
}
function drawPigeons(t, fr, crowd) {
  let win = null;
  for (const f of FLY) if (t >= f[0] && t < f[1] && !(f[2] && crowd === 0)) win = f;
  const s8 = step(t, 1920), s2 = step(t, 320);
  PIGEONS.forEach((Pg, i) => {
    const S = PIG_SPR[Pg.c];
    if (win && t >= win[0] + Pg.d) {
      const s = (t - win[0] - Pg.d) / (win[1] - win[0] - Pg.d), bl = smooth(0, 0.16, s) * (1 - smooth(0.78, 1, s));
      const th = TAU * (1.55 * s + i * 0.03 + Pg.ph * 0.04) + 2.1, ox = 196 + 128 * Math.cos(th), oy = 128 + 30 * Math.sin(th) + 5 * Math.sin(th * 3 + i);
      const x = lerp(Pg.x, ox, bl), y = lerp(Pg.y, oy, bl);
      ctx.drawImage(S[(s8 + i) & 1 ? 'up' : 'down'][Math.sin(th) > 0 ? 1 : 0], Math.round(x) - 2, Math.round(y) - 3);
      return;
    }
    const peck = hash(i, s2 + Math.floor(Pg.ph * 7), 55) < 0.4, hop = hash(i, step(t, 60), 56) < 0.2 ? 1 : 0;
    ctx.drawImage(S[peck ? 'peck' : 'stand'][Pg.flip ? 1 : 0], Pg.x - 2 + hop, Pg.y - 3);
  });
}

/* ================= Cats ================= */
const CAT_SIT = ['.e.e..', '.hhh..', '.hhh..', '.bbbb.', 'bbbbbt', 'bbbbbt'], CAT_WALK = [['.......e.e', 't......hhh', '.tbbbbbhh.', '..bbbbbb..', '..l.l.l.l.'], ['.......e.e', '.t.....hhh', '..tbbbbhh.', '..bbbbbb..', '...l.ll.l.']];
const CAT_COLS = [{ b: '#c8783a', h: '#d88a48', e: '#c8783a', t: '#a85a28', l: '#a85a28' }, { b: '#2a2830', h: '#2a2830', e: '#2a2830', t: '#1c1a22', l: '#e8e4dc' }, { b: '#8a8690', h: '#9a96a0', e: '#8a8690', t: '#6a6670', l: '#6a6670' }];
const CAT_TRIP = { a: 4, b: 11, c: 226, d: 233, from: [48, 239], to: [147, 247] };       // the church cat goes to watch the pigeons, and comes home
const ROOF_CAT = { a: 56, b: 88, x0: 338, x1: 224 };                                        // another walks the station's ridge
let CAT_SPR;
function buildCats() { CAT_SPR = CAT_COLS.map(k => ({ sit: [sprite(CAT_SIT, k), sprite(CAT_SIT, k, true)], walk: CAT_WALK.map(rw => [sprite(rw, k), sprite(rw, k, true)]) })); }
function drawSitCat(c, x, y, flip, t, seed) {
  ctx.drawImage(CAT_SPR[c].sit[flip ? 1 : 0], x - 3, y - 5);
  const sw = Math.round(wave(t, 30 + seed * 7, seed * 0.3) * 1.4);
  px(ctx, x + (flip ? -4 : 3) + (flip ? -1 : 1) * Math.max(0, sw), y - 2 - Math.abs(sw), CAT_COLS[c].t);
  if (frac(t / 6 + seed * 0.37) < 0.95) { px(ctx, x + (flip ? 1 : -2), y - 4, '#d8f070'); px(ctx, x + (flip ? -1 : 0), y - 4, '#d8f070'); }
}
function drawChurchCat(t, fr) {
  const C = CAT_TRIP, go = t >= C.a && t < C.b, back = t >= C.c && t < C.d;
  if (go || back) {
    const v = go ? (t - C.a) / (C.b - C.a) : 1 - (t - C.c) / (C.d - C.c);
    ctx.drawImage(CAT_SPR[0].walk[(fr >> 0) & 1][go ? 0 : 1], Math.round(lerp(C.from[0], C.to[0], v)) - 5, Math.round(lerp(C.from[1], C.to[1], v)) - 4);
  } else if (t >= C.b && t < C.c) drawSitCat(0, C.to[0], C.to[1], false, t, 1);
  else drawSitCat(0, C.from[0], C.from[1], false, t, 1);
}
function drawRoofCat(t, fr) {
  const C = ROOF_CAT;
  if (t < C.a || t >= C.b) return;
  const s = t - C.a, len = C.b - C.a;
  if (s > 12 && s < 19) { drawSitCat(2, Math.round(lerp(C.x0, C.x1, 12 / len)), STN.ridge - 1, true, t, 2); return; }   // a pause to look down at the square
  const x = Math.round(lerp(C.x0, C.x1, s <= 12 ? s / len : (12 + (s - 19) * (len - 12) / (len - 19)) / len));
  const rise = Math.min(1, s / 2.5, (len - s) / 2.5);
  ctx.drawImage(CAT_SPR[2].walk[fr & 1][1], x - 5, STN.ridge - 5 + Math.round((1 - rise) * 8));
}

/* ================= Fireworks over the rock (they go up from Plaka, behind it) ================= */
// [burst time, x, y, palette]
const FW = [];
{
  const r = rng(1453);
  let t = 124.5, k = 0;
  while (t < 166.6) {                                   // singles and pairs for half a minute, then the finale
    FW.push([+t.toFixed(2), Math.round(126 + r() * 244), Math.round(8 + r() * 40), k % 4, Math.round(26 + r() * 26)]);
    t += t > 159 ? 0.4 + r() * 0.4 : k % 3 === 2 ? 0.5 : 1.3 + r() * 1.1;
    k++;
  }
}
const FW_PAL = [['#fff4c8', '#ffc040', '#ff8a2a'], ['#ffe0ea', '#ff5a8a', '#c83a6a'], ['#e4ffe8', '#6ae08a', '#2a9a5a'], ['#e8eeff', '#7aa8ff', '#4a6ad8']];
const FW_RISE = 1.5, FW_LIFE = 2.7;
function drawFireworks(t, reducedMotion) {
  for (let k = 0; k < FW.length; k++) {
    const [bt, x, y, pal] = FW[k], d = t - bt;
    if (d < -FW_RISE || d > FW_LIFE) continue;
    if (d < 0) {                                        // the rocket climbing, a tail of sparks behind it
      const s = 1 + d / FW_RISE, e = 1 - (1 - s) * (1 - s), ry = lerp(108, y, e), rx = x + (1 - e) * (k & 1 ? 9 : -9);
      for (let q = 0; q < 5; q++) { ctx.globalAlpha = 1 - q * 0.2; px(ctx, Math.round(rx + (k & 1 ? q * 0.3 : -q * 0.3)), Math.round(ry) + q, q ? '#e8a050' : '#fff6d8'); }
      ctx.globalAlpha = 1;
      continue;
    }
    const s = d / FW_LIFE, R = FW[k][4], n = 30 + Math.round(R * 0.9), ease = 1 - Math.pow(1 - Math.min(1, s * 1.5), 3), fade = Math.pow(1 - s, 1.1);
    const willow = k % 4 === 0, ring2 = k % 3 === 1, st = step(t, 2400), C = FW_PAL[pal], tails = willow ? 5 : 3;
    if (s < 0.14 && !reducedMotion) halo(HL.fwCore, x, y, 1 - s / 0.14);
    halo(HL.fwGlow[pal], x, y + 4, fade * (reducedMotion ? 0.4 : 1));
    for (let i = 0; i < n; i++) {
      if (hash(k * 131 + i, st, 43) > fade * 1.9) continue;                                    // twinkling out
      const a = (i + hash(k, i, 41) * 0.7) / n * TAU, inner = ring2 && (i & 1), v = inner ? 0.5 : 0.78 + 0.22 * hash(k, i, 42), g = s * s * (willow ? 26 : 10);
      const dx = Math.cos(a) * R * v, dy = Math.sin(a) * R * v * 0.94;
      for (let q = tails; q >= 1; q--) {                 // the streak each star leaves
        const e2 = ease * (1 - q * 0.07);
        ctx.globalAlpha = fade * (1 - q / (tails + 1)) * 0.85; ctx.fillStyle = C[q > 1 ? 2 : 1];
        ctx.fillRect(Math.round(x + dx * e2), Math.round(y + dy * e2 + g * (1 - q * 0.12)), 1, 1);
      }
      ctx.globalAlpha = 1; ctx.fillStyle = s < 0.3 || inner ? C[0] : C[1];
      ctx.fillRect(Math.round(x + dx * ease), Math.round(y + dy * ease + g), 1, 1);
    }
    if (s > 0.35) for (let j = 0; j < 18; j++) {          // crackle: white sparks left hanging where it burst
      const hj = hash(k, j, 44), a = hj * TAU, rr = R * Math.sqrt(hash(k, j, 45));
      if (hash(k * 17 + j, st, 46) < 0.3 * fade) px(ctx, Math.round(x + Math.cos(a) * rr), Math.round(y + Math.sin(a) * rr + s * 8), '#ffffff');
    }
  }
}
// each burst lights the cloud, the temples and the rock for a moment (drawn over the Acropolis)
function drawFireworkGlow(t, reducedMotion) {
  for (let k = 0; k < FW.length; k++) {
    const [bt, x, y, pal] = FW[k], d = t - bt;
    if (d >= 0 && d < FW_LIFE) halo(HL.fw[pal], x, y + 20, Math.pow(1 - d / FW_LIFE, 1.6) * (reducedMotion ? 0.35 : 1));
  }
}

/* ================= Smoke off the grill ================= */
const SMOKE = [];
{ const r = rng(62); for (let i = 0; i < 16; i++) SMOKE.push({ n: 28 + Math.floor(r() * 8), ph: r(), k: 20 + Math.floor(r() * 20), p: r(), s: 0.7 + r() * 0.6 }); }
function puffs(list, t, x0, y0, rise, drift, rMax, alpha, pal) {
  for (const s of list) {
    const q = t * s.n / LOOP + s.ph, u = q - Math.floor(q);
    const x = x0 + (0.35 * u + 0.65 * u * u) * drift * s.s + wave(t, s.k, s.p) * 2 * u;
    const y = y0 - u * rise + u * u * rise * 0.25;
    const rr = Math.max(1, Math.min(rMax, 1 + Math.floor(u * (rMax + 1))));
    ctx.globalAlpha = alpha * Math.min(1, u * 14) * Math.pow(1 - u, 1.2);
    ctx.drawImage(pal[rr], Math.round(x) - rr, Math.round(y) - rr);
  }
  ctx.globalAlpha = 1;
}

/* ================= The orange bough overhead (top right), white petals coming down ================= */
function renderBough() {
  const w = 118, h = 54, p = painter(w, h), r = rng(515);
  const twig = (x0, y0, x1, y1) => { for (let k = 0; k <= 40; k++) { const f = k / 40; p.put(lerp(x0, x1, f), lerp(y0, y1, f) + Math.sin(f * 3) * 2, '#2a1e1a'); p.put(lerp(x0, x1, f), lerp(y0, y1, f) + Math.sin(f * 3) * 2 + 1, '#1a1214'); } };
  twig(w, 4, 30, 22); twig(w, 14, 54, 40); twig(86, 10, 60, 2); twig(100, 12, 84, 44);
  for (const [cx, cy, rx, ry] of [[36, 22, 15, 8], [58, 38, 14, 8], [84, 14, 18, 10], [104, 30, 15, 12], [70, 6, 16, 6], [110, 8, 10, 8], [90, 44, 10, 6], [16, 20, 9, 5]]) {
    for (let y = -ry; y <= ry; y++) for (let i = -rx; i <= rx; i++) {
      if (Math.hypot(i / rx, y / ry) > 1 - hash(cx + i, cy + y, 96) * 0.45) continue;
      const v = 0.35 + (y / ry) * 0.3 - (i / rx) * 0.12 + (hash((cx + i) >> 1, (cy + y) >> 1, 97) - 0.5) * 0.55;       // lit from below by the square
      p.put(cx + i, cy + y, pick(LEAF, v, cx + i, cy + y));
    }
    for (let k = 0; k < 7; k++) {
      const x = Math.round(cx + (r() - 0.5) * 2 * (rx - 2)), y = Math.round(cy + (r() - 0.5) * 2 * (ry - 2));
      if (k < 2) { p.box(x, y, 3, 3, '#ff9a2a'); p.put(x, y, '#ffc060'); p.put(x + 2, y + 2, '#c85a18'); p.put(x + 2, y, '#e0701a'); p.put(x, y + 2, '#e0701a'); }
      else { p.put(x, y, '#fffaf0'); p.put(x + 1, y, '#f0e4d4'); if (r() < 0.6) p.put(x, y + 1, '#e8d8c8'); }
    }
  }
  return p.done();
}
function drawBough(t) {
  const sw = wave(t, 16, 0.1) * 1.3 + wave(t, 41, 0.6) * 0.7;
  for (let k = 0; k < 3; k++) ctx.drawImage(BOUGH_C, 0, k * 18, 118, 18, W - 116 + Math.round(sw * (k + 1) / 2), k * 18 - 2, 118, 18);
}
// 'nerantzi': orange-blossom petals at three depths. Far ones leave the trees on the square, the rest come from above.
const PETALS = [
  { count: 42, nMin: 22, nMax: 34, col: '#d4cee0', a: 0.85, amp: 3, drift: -16 },
  { count: 48, nMin: 12, nMax: 18, col: '#f6f0f2', a: 0.95, amp: 5, drift: -30, y1: [204, 258] },
  { count: 24, nMin: 14, nMax: 20, col: '#fffaf6', a: 1, amp: 9, drift: -60, y1: [H + 6, H + 6] },
];
PETALS.forEach((L, li) => {
  const r = rng(700 + li);
  L.list = [];
  for (let i = 0; i < L.count; i++) L.list.push({ id: i + li * 1000, n: L.nMin + Math.floor(r() * (L.nMax - L.nMin + 1)), ph: r(), k: 20 + Math.floor(r() * 50), p: r() });
});
function drawPetals(li, t) {
  const L = PETALS[li];
  for (const f of L.list) {
    const { cyc, u } = cycle(t, f.n, f.ph), sway = Math.sin(TAU * (f.k * t / LOOP + f.p));
    let x, y;
    if (li === 0) {
      const T = TREES[(f.id + cyc) % TREES.length], y0 = T.base - 24 - hash(f.id, cyc, 3) * T.r;
      x = T.x + (hash(f.id, cyc, 1) - 0.5) * 2 * T.r + u * L.drift + L.amp * sway * u; y = lerp(y0, T.base + 1 + hash(f.id, cyc, 2) * 9, u * u * 0.4 + u * 0.6);
    } else {
      const yEnd = lerp(L.y1[0], L.y1[1], hash(f.id, cyc, 2));
      x = (li === 2 ? 200 : -10) + hash(f.id, cyc, 1) * (li === 2 ? 340 : W + 50) + u * L.drift + L.amp * sway; y = -5 + u * (yEnd + 5);
    }
    ctx.globalAlpha = L.a * Math.min(1, u * 8) * (li < 2 && u > 0.88 ? (1 - u) / 0.12 : 1);
    ctx.fillStyle = (f.id & 3) === 0 ? '#f8dce0' : L.col;
    if (li === 2) { const flat = Math.cos(TAU * (f.k * 2 * t / LOOP + f.p)) > 0; ctx.fillRect(Math.round(x), Math.round(y), flat ? 3 : 1, flat ? 1 : 2); }
    else ctx.fillRect(Math.round(x), Math.round(y), 1, 1);
  }
  ctx.globalAlpha = 1;
}

/* ================= The rooftop sign: a summer cinema under the stars ================= */
// A steel frame on a Plaka roof behind the station, right under the Parthenon: two posts, a braced board, three lamps on
// goose necks. The city's own ad says THERINO SINEMA (the open-air cinemas that open with the warm nights) next to a
// little screen on which a film flickers. render(t, { pitch }) paints the site's face instead; billboard: false leaves
// the whole thing out.
const BB_LAMPS = [BB.x + 8, BB.x + 24, BB.x + 40];
const BB_FLICK = [[47, 48.2], [171.5, 172.6]];          // the middle lamp stutters now and then
const BBC = { x: BB.x - 5, y: BB.y - 8, w: BB.w + 10, h: BB.h + 14 };
function renderBillboard() {
  const p = painter(BBC.w, BBC.h), put = (i, y, col) => p.put(i - BBC.x, y - BBC.y, col);
  const X0 = BB.x - 2, X1 = BB.x + BB.w + 1, Y0 = BB.y - 2, Y1 = BB.y + BB.h + 1;
  for (const px0 of [BB.x + 6, BB.x + BB.w - 8]) for (let y = Y1 + 1; y < BBC.y + BBC.h; y++) { put(px0, y, '#5a5866'); put(px0 + 1, y, '#2a2834'); }
  for (let i = BB.x + 8; i < BB.w + BB.x - 8; i++) { const f = (i - BB.x - 8) / (BB.w - 17); put(i, Math.round(lerp(Y1 + 1, Y1 + 5, f)), '#3a3846'); put(i, Math.round(lerp(Y1 + 5, Y1 + 1, f)), '#3a3846'); }
  for (let i = X0; i <= X1; i++) { put(i, Y0, '#2a2834'); put(i, Y0 + 1, '#6a6876'); put(i, Y1, '#1a1822'); put(i, Y1 - 1, '#4a4856'); }
  for (let y = Y0; y <= Y1; y++) { put(X0, y, '#2a2834'); put(X0 + 1, y, '#6a6876'); put(X1, y, '#1a1822'); put(X1 - 1, y, '#4a4856'); }
  for (const lx of BB_LAMPS) { for (let y = Y0 - 4; y < Y0; y++) put(lx + 2, y, '#3a3846'); put(lx + 1, Y0 - 5, '#3a3846'); put(lx, Y0 - 5, '#3a3846'); put(lx - 1, Y0 - 4, '#5a5866'); put(lx, Y0 - 4, '#5a5866'); }
  return p.done();
}
const BB_STARS = [[23, 1], [34, 9], [45, 2], [19, 9], [41, 17], [27, 17], [46, 10]];
function renderBillboardAd() {
  const { w, h } = BB, p = painter(w, h);
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) p.put(i, y, pick(['#161c4a', '#101440', '#0b0e30'], y / h + Math.hypot(i - 24, 0) / 90, i, y));
  for (let y = 2; y < 13; y++) for (let i = 2; i < 18; i++) p.put(i, y, y === 2 || y === 12 || i === 2 || i === 17 ? '#c8c0b0' : '#f4f0e0');     // the screen
  p.put(9, 13, '#8a8478'); p.put(10, 13, '#8a8478');
  for (let i = 2; i < 18; i += 2) { p.put(i, 16, '#8a3a44'); p.put(i + 1, 18, '#6a2a34'); p.put(i, 15, '#2a1a24'); }                           // rows of heads
  const c = p.done();
  greek(p.x, 'QEPINO', 21, 3, '#ffe9a0'); greek(p.x, 'SINEMA', 21, 11, '#ff9a6a');
  return c;
}
function drawBillboardAd(t) {
  ctx.drawImage(BB_AD, BB.x, BB.y);
  const x = BB.x + 3, y = BB.y + 3, sc = step(t, 60) % 3, s = step(t, 2880);
  if (sc === 0) {                                      // a sunset over the sea, a sail crossing
    rect(ctx, x, y, 14, 5, '#ffb060'); rect(ctx, x, y + 5, 14, 4, '#3a6ab0'); rect(ctx, x + 5, y + 3, 4, 2, '#fff0a0');
    const bx = x + 1 + Math.floor(frac(t / 4) * 11); rect(ctx, bx, y + 4, 3, 1, '#2a1a24'); px(ctx, bx + 1, y + 3, '#f4f0e0'); px(ctx, bx + 1, y + 2, '#f4f0e0');
  } else if (sc === 1) {                               // two faces lean together
    rect(ctx, x, y, 14, 9, '#c8d0e0'); const lean = Math.round(smooth(0.2, 0.8, frac(t / 4)) * 2);
    rect(ctx, x + 1 + lean, y + 2, 4, 7, '#2a1a24'); rect(ctx, x + 2 + lean, y + 1, 3, 2, '#2a1a24'); rect(ctx, x + 9 - lean, y + 3, 4, 6, '#3a2430'); rect(ctx, x + 9 - lean, y + 2, 3, 2, '#3a2430');
  } else { rect(ctx, x, y, 14, 9, '#1a1a24'); text(ctx, 'FIN', x + 2, y + 2, '#f4f0e0'); }
  ctx.globalAlpha = 0.3; rect(ctx, x, y + (s % 9), 14, 1, '#ffffff'); ctx.globalAlpha = 1;                                                         // the projector's flicker
  if ((s & 7) === 0) px(ctx, x + (s * 7) % 14, y + (s * 3) % 9, '#2a1a24');
  BB_STARS.forEach(([sx, sy], k) => { if (wave(t, 40 + k * 9, k * 0.23) > 0.1) px(ctx, BB.x + sx, BB.y + sy, k % 3 ? '#fff6d8' : '#a8c0ff'); });
}
function bbLampI(t, i) {
  let v = 0.95 + 0.05 * wave(t, 1800, 0.25 + i * 0.33);
  if (i === 1 && inEvent(t, BB_FLICK)) { const hv = hash(17, Math.floor(t * 24)); v *= hv > 0.5 ? 1 : hv > 0.22 ? 0.4 : 0.1; }
  return v;
}
function drawBillboard(t, pitch) {
  halo(HL.bbWash, BB.x + (BB.w >> 1), BB.y + 4, 0.8);
  ctx.drawImage(BB_C, BBC.x, BBC.y);
  if (!PITCH.covers(t, pitch)) drawBillboardAd(t);
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  BB_LAMPS.forEach((lx, i) => {
    const li = bbLampI(t, i);
    if (li < 0.9) { ctx.globalAlpha = (0.9 - li) * 0.5; rect(ctx, lx - 8, BB.y, 17, 8, '#06060e'); ctx.globalAlpha = 1; }
    ctx.fillStyle = li > 0.6 ? '#fff6d8' : li > 0.2 ? '#c8a070' : '#4a3a30';
    ctx.fillRect(lx - 1, BB.y - 5, 2, 1);
    halo(HL.bbLamp, lx, BB.y - 4, li);
  });
}

/* ================= The picked weather (site/js/engine/weather.js) ================= */
// Athens draws only its own blossom ('nerantzi'), so every pick is the engine's: far drops fall behind the rock, the
// middle ones onto the square, the nearest past the bottom of the frame; they catch the lamps and the lit doors.
const WEATHER = weatherPlans(['nerantzi']);
const WX_LAND = [[112, 150], [196, 254], [H + 4, H + 4]];
const WX_LIGHTS = [
  ...LAMPS.map(l => ({ x: l.x, y: l.head + 14, rx: 24, ry: 28, col: '#ffdca0' })), { x: CART.x + 17, y: CART.y - 18, rx: 16, ry: 14, col: '#ffe6b0' },
  { x: STN.doors[2], y: 178, rx: 48, ry: 13, col: '#ffe8b8' }, { x: SOUV.x + 14, y: 180, rx: 22, ry: 12, col: '#ffdca0' }, { x: 26, y: 224, rx: 22, ry: 18, col: '#ffd898' },
  { x: DUO.x + 19, y: DUO.y - 6, rx: 14, ry: 10, col: '#ffd898', duo: true },
];
const WX = { land: WX_LAND, lights: WX_LIGHTS };
const WX_BB = { land: WX_LAND, lights: [...WX_LIGHTS, { x: BB.x + (BB.w >> 1), y: BB.y + 3, rx: 30, ry: 10, col: '#fff0c8' }] };
// crowd 0: no duo, so no lantern of theirs for the drops to catch
const WX_NONE = { land: WX_LAND, lights: WX.lights.filter(l => !l.duo) }, WX_BB_NONE = { land: WX_LAND, lights: WX_BB.lights.filter(l => !l.duo) };

/* ================= Vignette ================= */
function renderVignette() {
  const [c, x] = makeCanvas(W, H), img = x.createImageData(W, H), d = img.data;
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.78, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q <= 0) continue;
    const o = (y * W + i) * 4; d[o] = 4; d[o + 1] = 4; d[o + 2] = 16; d[o + 3] = Math.round(q * 0.42 * 255);
  }
  x.putImageData(img, 0, 0);
  return c;
}

/* ================= Per-frame: the town's lights, the trees and lamps, light on the stones ================= */
function makePool(rx, ry, col, maxA) {                  // a flattened dithered ellipse of light on the ground (drawn additively)
  const rgb = hex(col), w = rx * 2 + 1, h = ry * 2 + 1, [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) {
    const dd = Math.hypot((i - rx) / rx, (y - ry) / ry);
    if (dd >= 1) continue;
    const q = Math.floor(Math.pow(1 - dd, 1.5) * 5 + bayer(i, y)) / 5, o = (y * w + i) * 4;
    d[o] = rgb[0]; d[o + 1] = rgb[1]; d[o + 2] = rgb[2]; d[o + 3] = Math.round(Math.min(1, q) * maxA * 255);
  }
  x.putImageData(img, 0, 0);
  return { c, rx, ry };
}
function drawPool(pl, x, y, a) {
  if (a <= 0.004) return;
  ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = Math.min(1, a);
  ctx.drawImage(pl.c, Math.round(x) - pl.rx, Math.round(y) - pl.ry);
  ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
}
function drawTownFx(t, crowd) {
  for (const w of TWIN) if (hash(w.id + 50, Math.floor(frac(t / LOOP + w.off) * w.n), 4) < 0.5) {
    ctx.fillStyle = '#ffe0a0'; ctx.fillRect(w.x, w.y, w.w, 1);
    ctx.fillStyle = '#f4b060'; ctx.fillRect(w.x, w.y + 1, w.w, w.h - 1);
  }
  const tv = step(t, 1440);
  TVWIN.forEach((w, k) => { ctx.fillStyle = ['#5a78b8', '#8aa4dc', '#3e4e8c', '#b4c4ec', '#6a5a9c'][Math.floor(hash(k, tv >> (k & 1), 8) * 5)]; ctx.fillRect(w.x, w.y + 1, 2, 3); });
  const s = step(t + 0.11, 480);
  for (let i = 0; i < STRINGS.length; i++) {            // the light strings: warm bulbs, one in five coloured, twinkling
    const b = STRINGS[i], h = hash(i, s, 61);
    ctx.fillStyle = h < 0.1 ? '#6a4a28' : b.c && i % 3 === 0 ? '#ff7a5a' : b.c && i % 3 === 1 ? '#6ac8ff' : h < 0.2 ? '#ffffff' : '#ffe08a';
    ctx.fillRect(b.x, b.y, 1, 1);
  }
  LANE_LAMPS.forEach((l, k) => {                        // the flea market's glass lamps, swinging a little
    const x = l.x + Math.round(wave(t, 28 + k * 5, l.ph) * 0.8), g = 0.75 + 0.25 * wave(t, 90 + k * 13, l.ph);
    rect(ctx, x - 1, l.y, 3, 3, l.col); px(ctx, x, l.y + 1, '#fff6e0'); px(ctx, x, l.y + 3, '#3a3040');
    halo(HL.lane[k % HL.lane.length], x, l.y + 1, g);
  });
  for (const d of STN.doors.slice(1, 4)) halo(HL.stn, d, 178, 0.85 + 0.08 * wave(t, 50, d * 0.01));
  halo(HL.mosq, MOSQ.cx, 150, 0.9); halo(HL.shop, MOSQ.x0 + 13, 180, 0.8); halo(HL.shop, MOSQ.x0 + 35, 180, 0.8);
  halo(HL.shop, PORCH_ARCH[1] + 3, 160, 0.6 + 0.1 * wave(t, 70, 0.3)); halo(HL.shop, 194, 176, 0.7); halo(HL.shop, 59, 178, 0.7);
  halo(HL.stn, SOUV.x + 14, 178, 0.9); halo(HL.shop, 436, 180, 0.8); halo(HL.stn, 464, 178, 0.7);
  halo(HL.roof, BLOCK.x0 + 46, BLOCK.top - 16, 0.8 + 0.1 * wave(t, 20, 0.5)); halo(HL.roof, 30, LEFTB.top - 14, 0.7);
  drawCook(t, crowd);
}
function drawTree(i, t) {
  const T = TREES[i], c = TREE_C[i], sw = Math.round(wave(t, 14 + i * 3, i * 0.31) * 0.7);
  ctx.drawImage(c, 0, c.height - 22, c.width, 22, T.x - T.r - 2, T.base - 21, c.width, 22);                      // trunk
  ctx.drawImage(c, 0, 0, c.width, c.height - 22, T.x - T.r - 2 + sw, T.base - c.height + 1, c.width, c.height - 22);   // the crown stirs
}
function drawBlossoms(t) { for (const b of BLOSSOMS) if (wave(t, b.k, b.ph) > 0.55) px(ctx, b.x, b.y, '#fffdf4'); }
function drawLamp(L, t) {
  ctx.drawImage(LAMP_C, L.x - 6, L.base - 57);
  const fl = 0.93 + 0.05 * wave(t, 700, L.x * 0.01) + 0.02 * wave(t, 1930, 0.3);
  halo(HL.lamp, L.x, L.base - 49, fl);
  halo(HL.lampCore, L.x - 5, L.base - 49, fl); halo(HL.lampCore, L.x + 5, L.base - 49, fl);
}
function drawGroundFx(t, reducedMotion) {
  drawPool(POOL.door, 34, 245, churchLight(t) * 0.9);   // the church door's light on the stones goes out with it
  const fw = FW.reduce((m, [bt]) => Math.max(m, t >= bt && t < bt + FW_LIFE ? Math.pow(1 - (t - bt) / FW_LIFE, 2) : 0), 0);
  if (fw > 0 && !reducedMotion) drawPool(POOL.sky, 240, 232, fw * 0.5);     // the bursts wash over the square
}

/* ================= Pre-render everything static ================= */
let HL, POOL, SKY_C, MOON_C, CLOUDS, FAR_C, ACRO_C, TOWN_C, BARE_C, CHURCH_C, TREE_C, LAMP_C, CART_C, BOUGH_C, VIG_C, BB_C, BB_AD, PITCH, PUFF_S, ACTORS;
function prerender() {
  HL = {
    moon: makeHalo(26, '#dfe4f0', 0.16, 1.5), acro: makeHalo(150, '#ffb45a', 0.1, 1.4), temple: makeHalo(60, '#ffd890', 0.1, 1.5),
    templeS: makeHalo(22, '#ffe0a0', 0.12, 1.5), rockLamp: makeHalo(7, '#fff0c8', 0.45, 1.4), candle: makeHalo(6, '#ffc060', 0.5, 1.3),
    flame: makeHalo(12, '#ffd080', 0.55, 1.4), lantern: makeHalo(13, '#ffb860', 0.35, 1.4), phone: makeHalo(4, '#cfe0ff', 0.4, 1.2),
    flash: makeHalo(13, '#ffffff', 0.9, 1.2), grill: makeHalo(8, '#ff8a3a', 0.4, 1.3), bulb: makeHalo(24, '#ffd890', 0.22, 1.5),
    bulbCore: makeHalo(6, '#fff4d0', 0.5, 1.3), door: makeHalo(26, '#ffc070', 0.3, 1.5), icon: makeHalo(5, '#ff6a3a', 0.45, 1.3),
    fwCore: makeHalo(17, '#ffffff', 0.8, 1.2), fw: FW_PAL.map(c => makeHalo(150, c[1], 0.2, 1.15)), fwGlow: FW_PAL.map(c => makeHalo(46, c[1], 0.3, 1.4)), lamp: makeHalo(42, '#ffd49a', 0.16, 1.6),
    lampCore: makeHalo(7, '#fff4dc', 0.5, 1.3), bbLamp: makeHalo(6, '#ffe8c0', 0.5, 1.5), bbWash: makeHalo(30, '#ffe0b0', 0.1, 1.5),
    stn: makeHalo(20, '#ffe0a0', 0.16, 1.5), mosq: makeHalo(46, '#ffc880', 0.1, 1.5), shop: makeHalo(14, '#ffc070', 0.2, 1.4),
    roof: makeHalo(44, '#ffc880', 0.1, 1.5), lane: ['#ff8a4a', '#4ad0c8', '#ffd24a', '#e85ab0', '#6a9aff', '#8ae06a', '#ff5a4a'].map(c => makeHalo(6, c, 0.45, 1.3)),
  };
  POOL = { door: makePool(38, 11, '#ffb860', 0.4), sky: makePool(230, 40, '#ffd0a0', 0.16) };
  SKY_C = renderSky(); MOON_C = renderMoon(); CLOUDS = CLOUD_DEFS.map(makeCloudLayer);
  FAR_C = renderFar(); ACRO_C = renderAcropolis(); TOWN_C = renderTown(); BARE_C = renderBare(); CHURCH_C = renderChurch();
  TREE_C = TREES.map(renderTree); LAMP_C = renderLamp(); CART_C = renderCart(); BOUGH_C = renderBough();
  BB_C = renderBillboard(); BB_AD = renderBillboardAd(); PITCH = makePitch(BB.w, BB.h, { accent: '#8f9c4d' });
  PUFF_S = [null]; for (let r = 1; r <= 9; r++) PUFF_S.push(makePuff(r, '#d6ccc8', '#9a929c'));
  VIG_C = renderVignette();
  buildWalkers(); buildDuo(); buildListeners(); buildDancers(); buildGuests(); buildQueue(); buildVendors(); buildKids(); buildShooters();
  buildBalloons(); buildTerrace(); buildSitters(); buildChurchFx(); buildPigeons(); buildCats(); buildScooters();
  for (const C of COMMUTERS) C.spr = walkSet(C.S, C.dir < 0);
  // everyone and everything standing on the square, sorted by the row of their feet (far to near)
  // (each entry carries its crowd rank: 0 = always drawn, everything that is not a person; CAST = the essential cast,
  // drawn at every level but crowd 0. The stand, the tables, the window and the pigeons are told the level instead)
  ACTORS = [];
  const A = (y, draw, rank = 0) => ACTORS.push({ y, draw, rank });
  WALKERS.forEach((w, i) => A(w.y, (t, fr) => drawWalker(w, i, t, fr), w.rank));
  LISTENERS.forEach((L, i) => A(L[1], t => drawListener(L, i, t), L.rank));
  DANCERS.forEach((D, i) => A(DANCE.y, (t, fr) => drawDancer(D, i, t, fr), CAST));
  GUESTS.forEach((G, i) => A(G.y, (t, fr) => drawGuest(G, i, t, fr), G.rank));
  SHOOTERS.forEach((S, i) => A(S.y, t => drawShooter(S, i, t), S.rank));
  TABLES.forEach((T, k) => A(T.y, (t, fr, crowd) => drawTable(T, k, t, crowd)));
  SITTERS.forEach((L, i) => A(L[1], t => drawSitter(L, i, t), L.rank));
  COMMUTERS.forEach((C, i) => A(C.y, (t, fr) => drawCommuter(C, i, t, fr), C.rank));
  TREES.forEach((T, i) => A(T.base, t => drawTree(i, t)));
  LAMPS.forEach(L => A(L.base, t => drawLamp(L, t)));
  A(DUO.y + 1, drawDuo, CAST); A(QUEUE.y, drawQueue, CAST); A(CART.y - 3, drawFruitSeller, CAST); A(CART.y, drawCart); A(198, drawKoulouri);
  A(KIDS.y + 1, drawKids, CAST); A(BSELL.y, drawBalloonSeller, CAST); A(200, drawWaiter, CAST); A(CH.foot, (t, fr, crowd) => drawChurch(t, crowd)); A(PRIEST.y, drawPriest, CAST);
  A(RIDE.y, drawRider); A(205, drawParked);
  A(246, drawPigeons); A(247, drawChurchCat); A(223, t => drawSitCat(1, 432, 223, true, t, 3));
  ACTORS.sort((a, b) => a.y - b.y);
}

/* ================= Frame compositor (back to front) ================= */
function drawScroll(cv, y, k, t) {
  const off = Math.floor(frac(k * t / LOOP) * W);
  ctx.drawImage(cv, off - W, y); ctx.drawImage(cv, off, y);
}
// pitch: the site's face on the sign; billboard: false leaves the sign out (the loop videos); weather: a WEATHERS pick
// (site/js/engine/weather.js), the city's own blossom when left out; crowd: the share of the crowd to show, 0..1
// (CROWD when left out or not a number)
function renderFrame(t, { pitch = false, billboard = true, weather, reducedMotion = false, crowd: share } = {}) {
  const crowd = clamp(typeof share === 'number' && share === share ? share : CROWD, 0, 1);
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const none = crowd === 0;                             // nobody on the square
  const wx = WEATHER(weather), wxc = none ? (billboard ? WX_BB_NONE : WX_NONE) : billboard ? WX_BB : WX, fr = step(t, 1200);
  // sky: stars, the moon, three decks of cloud
  ctx.drawImage(SKY_C, 0, 0);
  ctx.fillStyle = '#e4e8ff';
  for (const s of STARS) { ctx.globalAlpha = s.b * (0.55 + 0.45 * wave(t, s.k, s.ph)); ctx.fillRect(s.x, s.y, 1, 1); }
  ctx.globalAlpha = 1;
  halo(HL.moon, MOON.x, MOON.y, 0.9);
  ctx.drawImage(MOON_C, MOON.x - MOON.r, MOON.y - MOON.r);
  for (const L of CLOUDS) drawScroll(L.c, L.y, L.k, t);
  ctx.drawImage(FAR_C, 0, 0);
  for (const f of FAR_LIGHTS) if (wave(t, f.k, f.ph) > 0.3) px(ctx, f.x, f.y, '#ffd890');
  drawFireworks(t, reducedMotion);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  // the Acropolis
  halo(HL.acro, 245, 84, 1);
  ctx.drawImage(ACRO_C, 0, 0);
  drawRockFx(t);
  drawFireworkGlow(t, reducedMotion);
  // Plaka and the buildings round the square, the sign, smoke, small figures
  ctx.drawImage(TOWN_C, 0, 0);
  if (none) ctx.drawImage(BARE_C, BARE.x, BARE.y);      // no duo: their lantern's pool is not on the stones
  drawTownFx(t, crowd);
  if (billboard) drawBillboard(t, pitch);
  drawRoofCat(t, fr);
  puffs(SMOKE, t, SOUV.x + 6, 163, 64, -24, 7, 0.5, PUFF_S);
  drawSmallFolk(t, crowd);
  if (wx.nerantzi) drawPetals(0, t);
  // the square and everyone on it (those the crowd level thins away are skipped)
  drawGroundFx(t, reducedMotion);
  for (const a of ACTORS) if (seen(a.rank, crowd)) a.draw(t, fr, crowd);
  drawBlossoms(t);
  if (wx.nerantzi) drawPetals(1, t);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  if (!none) drawEscaped(t);                            // the balloon that gets away is the seller's
  // the orange bough overhead, the nearest petals
  drawBough(t);
  if (wx.nerantzi) drawPetals(2, t);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'athens',
  name: 'Athens',
  tagline: 'Easter under the Acropolis',
  timeZone: 'Europe/Athens',
  accent: '#8f9c4d',                                   // olive, written out: tools/seo/seo.mjs reads it from the source
  focusX: 244,
  landmark: [PAR.x0 - 3, PAR.fx1 + 3],                 // the Parthenon's steps (the Erechtheion stands in front of it)
  landmarkTop: PAR.apex,                               // the row of the west pediment's apex
  billboard: BB,
  crowd: CROWD,                                        // the share of the crowd shown by default; render(t, { crowd }) takes 0..1
  ambience: { crowd: 0.55, traffic: 0.08, wind: 0.08 },
  events: [
    ...[14, 74, 134, 194].map(t => ({ t, type: 'train', dur: 9, pan: [-0.25, 0.35], gain: 0.2 })),     // the electric railway under the square
    { t: FLAME, type: 'chime', x: 30, gain: 0.3 },
    ...[RIDE.t0 + 2, RIDE.t0 + RIDE.every + 2].map(t => ({ t, type: 'horn', x: 340, gain: 0.14 })),                  // the delivery scooter's toot
    ...BELLS.map(t => ({ t, type: 'bell', x: BELL.x, gain: 0.5 })),
    ...FW.filter((f, k) => k % 2 === 0 || f[0] > 159).map(([t, x, , , R]) => ({ t, type: 'firework', x, gain: +(0.3 + R / 130).toFixed(2) })),
  ].sort((a, b) => a.t - b.t),
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
